<template>
  <v-container
    fluid
    class="release-notes-page pa-0"
  >
    <v-row class="no-gutters">
      <!-- Sticky Sidebar TOC -->
      <v-col
        class="toc-sidebar d-none d-md-block"
        cols="auto"
        style="width: 220px; min-width: 220px;"
      >
        <div class="toc-sticky">
          <div :class="`toc-header caption font-weight-bold text-uppercase px-4 pt-4 pb-2 ${nearblackTextClass}`">
            Jump to
          </div>
          <v-list
            dense
            nav
            class="toc-list py-0"
          >
            <v-list-item
              v-for="section in tocSections"
              :key="section.id"
              :class="{ 'toc-active': activeSection === section.id }"
              class="toc-item"
              @click="scrollToSection(section.id)"
            >
              <v-list-item-content>
                <v-list-item-title class="caption">
                  {{ section.label }}
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>
        </div>
      </v-col>

      <!-- Main Content -->
      <v-col>
        <v-card class="py-9 px-12 elevation-0">
          <v-card-title class="primary--text headline">
            Release Notes
          </v-card-title>

          <v-card-subtitle class="subtitle-1">
            What's New in iAdOps?
          </v-card-subtitle>

          <v-card-text class="font-weight-medium">
            <v-alert
              v-if="isInternalUser"
              outlined
              color="info"
              class="mb-4"
            >
              Internal users can open the dedicated Release Kit page from the Docs sidebar
              to copy stakeholder-ready launch communication templates.
            </v-alert>

            <v-alert
              outlined
              color="info"
              class="mb-4"
            >
              Looking for a top-level overview? Open
              <router-link
                to="/docs/release-summary"
                class="font-weight-bold"
              >
                Release Summary
              </router-link>
              for executive highlights since v4.72.3.
            </v-alert>

            <!-- August 2026 -->
            <div
              id="2026-08"
              class="release-section"
            >
              <h3 :class="`mb-1 subtitle-1 font-weight-bold release-month-header ${nearblackTextClass}`">
                August 2026
              </h3>

              <div class="release-entry">
                <h4>
                  <span class="release-badge purple white--text">Update</span>
                  "New Content Available" Prompt
                </h4>
                <p>
                  When we release an update, you may now see a "New Content Available"
                  prompt inviting you to refresh. This isn't automatic or forced &mdash;
                  you can click Refresh right away, or Cancel to finish what you're doing
                  and refresh later.
                </p>
                <p>
                  We recommend refreshing when you see it, especially for
                  security-related updates, so you're always running the latest
                  version. If you have unsaved work in a form, save or submit it first,
                  since refreshing reloads the page.
                </p>
              </div>
            </div>

            <!-- June 2026 -->
            <div
              id="2026-06"
              class="release-section"
            >
              <h3 :class="`mb-1 subtitle-1 font-weight-bold release-month-header ${nearblackTextClass}`">
                June 2026
              </h3>
              <p class="caption coolgray--text mb-4">
                Shipped June 3, 2026
              </p>

              <div
                v-if="isInternalUser"
                class="release-entry"
              >
                <h4>
                  <span class="release-badge primary white--text">New</span>
                  Engagement Model Admin Surfaces (Phase 2)
                </h4>
                <p>
                  Added Engagement Model admin management screens for
                  Engagement Types, Contract Types, and Assignment Rules,
                  including create/update/delete workflows, active-state
                  controls, and rule-priority reordering in the Admin area.
                </p>
              </div>

              <div
                v-if="isInternalUser"
                class="release-entry"
              >
                <h4>
                  <span class="release-badge primary white--text">New</span>
                  Engagement Admin Backend CRUD Wiring
                </h4>
                <p>
                  Implemented backend admin dispatch handlers for
                  engagementType, contractType, and assignmentRule actions,
                  plus syncProduction routing. This enables persisted CRUD
                  operations from the new admin surfaces through the shared
                  admin endpoint model.
                </p>
              </div>

              <div
                v-if="isInternalUser"
                class="release-entry"
              >
                <h4>
                  <span class="release-badge toasted white--text">Fix</span>
                  Admin Users View Loads on Initial Navigation
                </h4>
                <p>
                  Fixed a race condition where the Admin Users view would fail
                  to load on first visit because it fired the data request
                  before the authentication token was ready. Users now load
                  automatically after auth initialization completes.
                </p>
              </div>

              <div
                v-if="isInternalUser"
                class="release-entry"
              >
                <h4>
                  <span class="release-badge toasted white--text">Fix</span>
                  Person Add Button Opens Create Modal Again
                </h4>
                <p>
                  Restored the create-person modal wiring in the Person view so
                  internal admins can add people directly from the "Add Person"
                  action without a dead click state.
                </p>
              </div>

              <div
                v-if="isInternalUser"
                class="release-entry"
              >
                <h4>
                  <span class="release-badge toasted white--text">Fix</span>
                  Timesheet Date Pickers Open Reliably in Chrome
                </h4>
                <p>
                  Updated timesheet date input activators to use wrapper
                  containers so readonly date fields open menus reliably in
                  Chrome for both entry-date selection and bulk-copy workflows.
                </p>
              </div>

              <div
                v-if="isInternalUser"
                class="release-entry"
              >
                <h4>
                  <span class="release-badge primary white--text">New</span>
                  Projects Columns Expanded for Ownership Visibility
                </h4>
                <p>
                  Projects table workflows now expose Account Manager, Project
                  Owner, and Project Support columns by default, with grouping
                  options aligned to the same ownership fields.
                </p>
              </div>

              <div
                v-if="isInternalUser"
                class="release-entry"
              >
                <h4>
                  <span class="release-badge primary white--text">New</span>
                  Estimated Project Value Field
                </h4>
                <p>
                  Added optional Estimated Project Value field to project forms,
                  cards, and tables for project prioritization and reporting.
                  Value displays with currency formatting and is sortable in
                  table view for easy project valuation analysis.
                </p>
              </div>

              <div
                v-if="isInternalUser"
                class="release-entry"
              >
                <h4>
                  <span class="release-badge primary white--text">New</span>
                  Recently Updated Projects Tab and Last Updated Tracking
                </h4>
                <p>
                  New "Recently Updated" tab surfaces projects modified in the
                  last 7 days, sorted by most recent. Each project card now
                  displays a human-readable "Last Updated" timestamp (e.g., "2
                  hours ago") for quick visibility into project activity.
                </p>
              </div>

              <div
                v-if="isInternalUser"
                class="release-entry"
              >
                <h4>
                  <span class="release-badge primary white--text">New</span>
                  Visual Indicators for New and Updated Projects
                </h4>
                <p>
                  Project cards now display "New" badges (blue) for projects
                  created within the last 7 days and "Updated" badges (green)
                  for projects modified within the last 24 hours. Badges
                  automatically appear and disappear based on time windows.
                  Optional "Status" and "Last Updated" columns available in
                  table view.
                </p>
              </div>

              <div
                v-if="isInternalUser"
                class="release-entry"
              >
                <h4>
                  <span class="release-badge primary white--text">New</span>
                  Unified Person Management View with Enhanced Modals
                </h4>
                <p>
                  Refactored person components with a centralized Person view
                  featuring tabbed filtering (Active, Archived, Pending), card-based
                  display, and redesigned create/edit modal with sectioned forms.
                  Person management now follows the same UI pattern as projects,
                  improving consistency and usability.
                </p>
              </div>

              <div
                v-if="isUserInternalDev"
                class="release-entry"
              >
                <h4>
                  <span class="release-badge darkteal white--text">Update</span>
                  Drata GCP Compliance Requirements and Validator
                </h4>
                <p>
                  Added
                  <code>docs/DRATA_GCP_REQUIREMENTS.md</code>
                  documenting exactly what Drata's cloud monitors require of
                  GCP objects (bucket versioning, Firestore storage alerting,
                  Pub/Sub message-age alerting), plus a new
                  <code>scripts/validate-gcp-compliance.mjs</code>
                  to re-check any project against those requirements. Used to
                  find and remediate three live failing Drata monitors on
                  <code>iadops-try</code>.
                </p>
              </div>

              <div
                v-if="isUserInternalDev"
                class="release-entry"
              >
                <h4>
                  <span class="release-badge darkteal white--text">Update</span>
                  Engagement Lifecycle State Machine Utility
                </h4>
                <p>
                  Added new
                  <code>engagementLifecycle.js</code>
                  utility for managing engagement state transitions, validation,
                  and business rule enforcement. Provides reusable functions for
                  state machine operations across engagement workflows.
                </p>
              </div>

              <div
                v-if="isUserInternalDev"
                class="release-entry"
              >
                <h4>
                  <span class="release-badge darkteal white--text">Update</span>
                  Theme System Enhancements and Accessibility Guidelines
                </h4>
                <p>
                  Enhanced hydrogen theme with semantic color tokens, improved
                  contrast ratios for WCAG AA compliance, and added new
                  <code>BRAND_ACCESSIBILITY_GUIDELINES.md</code>
                  documentation. Includes z-index hierarchy documentation and
                  spacing/typography scales for consistent component styling.
                </p>
              </div>

              <div
                v-if="isUserInternalDev"
                class="release-entry"
              >
                <h4>
                  <span class="release-badge darkteal white--text">Update</span>
                  Navigation Sidebar Reorganization
                </h4>
                <p>
                  Reordered admin sidebar to prioritize engagement tools before
                  projects, improving discoverability. Updated router dynamic
                  imports to support new admin surfaces and module structure.
                </p>
              </div>

              <div
                v-if="isUserInternalDev"
                class="release-entry"
              >
                <h4>
                  <span class="release-badge darkteal white--text">Update</span>
                  Log Investigation Runbook and GCP Query Helper
                </h4>
                <p>
                  Added a new incident triage workflow for support and
                  developers with a reusable Cloud Logging helper script and
                  runbook coverage that aligns Sentry error IDs with Firestore,
                  Firebase Auth, and GCP log evidence.
                </p>
              </div>

              <div
                v-if="isUserInternalDev"
                class="release-entry"
              >
                <h4>
                  <span class="release-badge toasted white--text">Fix</span>
                  Sentry HTTP Error Context and Grouping Quality
                </h4>
                <p>
                  Improved normalized HTTP error telemetry to avoid
                  <code>Error: undefined</code> captures, include richer
                  support context, and apply deterministic grouping fingerprints
                  for faster incident triage.
                </p>
              </div>
            </div>

            <!-- May 2026 -->
            <div
              id="2026-05"
              class="release-section"
            >
              <h3 :class="`mb-1 subtitle-1 font-weight-bold release-month-header ${nearblackTextClass}`">
                May 2026
              </h3>
              <p class="caption coolgray--text mb-4">
                Shipped May 1, 2026
              </p>

              <div class="release-entry">
                <h4>
                  <span class="release-badge toasted white--text">Fix</span>
                  Dashboard Theme Load Consistency
                </h4>
                <p>
                  Fixed an intermittent dashboard startup state where parts of
                  the page could render with mixed light and dark styling.
                  Theme selection now applies consistently during initial app
                  load and sign-in hydration.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge toasted white--text">Fix</span>
                  Settings Persistence and Theme Consistency
                </h4>
                <p>
                  Hardened Settings preference persistence so invalid payloads
                  are rejected before save and do not corrupt user preferences.
                  Shared admin, docs, navigation, filter, and time surfaces now
                  follow a more consistent theme path, reducing mixed
                  dark/light rendering after reloads and preference changes.
                </p>
              </div>

              <div
                v-if="isInternalUser"
                class="release-entry"
              >
                <h4>
                  <span class="release-badge primary white--text">New</span>
                  Time Last Month View and Expanded Export Fields
                </h4>
                <p>
                  Added a Last Month workflow in Time with aligned export fields
                  for downstream reporting. Teams can now review recent prior-
                  month activity with cleaner export parity.
                </p>
              </div>

              <div
                v-if="isInternalUser"
                class="release-entry"
              >
                <h4>
                  <span class="release-badge toasted white--text">Fix</span>
                  Time Guide Launch and Toolbar Spacing
                </h4>
                <p>
                  Fixed guide-launch edge behavior and tightened toolbar spacing
                  in Time for more consistent action visibility across viewports.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge primary white--text">New</span>
                  Theme-Aware Sign-In Page with OS Preference Detection
                </h4>
                <p>
                  Redesigned the sign-in page to automatically adapt to your
                  operating system's light/dark theme preference. The page now
                  includes theme-aware gradients, properly contrasted buttons,
                  and reactive form elements that match your system settings.
                  Sign-in remains consistent and accessible across all theme
                  modes.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge toasted white--text">Fix</span>
                  Sign-In Popup Blocker Message Accessibility
                </h4>
                <p>
                  Improved the popup blocker notification on the sign-in page for
                  better accessibility and clarity. The message now has proper color
                  contrast, larger readable text, center alignment, and more concise
                  wording: "Allow popups to sign in with Google or another provider."
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge toasted white--text">Fix</span>
                  Admin View Content Missing on Default Navigation
                </h4>
                <p>
                  Fixed an issue where navigating directly to the Admin section
                  showed a blank content area instead of the Enterprise tab.
                  The view now correctly displays the first accessible tab
                  when no specific sub-route is provided.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge toasted white--text">Fix</span>
                  Project Date Pickers Unresponsive in Chrome
                </h4>
                <p>
                  Fixed an issue where the Start Date and End Date pickers on
                  project cards were greyed out and unselectable in Chrome.
                  The form now uses a click-reliable activator pattern
                  compatible with all supported browsers.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge toasted white--text">Fix</span>
                  Sign-In Button Text Visibility in Dark Mode
                </h4>
                <p>
                  Fixed a contrast issue where sign-in button text could be
                  difficult to read in dark mode. Button colors now explicitly
                  adapt to both light and dark themes, ensuring readable text
                  and proper contrast ratios in all scenarios.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge toasted white--text">Fix</span>
                  Deactivated Account Sign-In Messaging
                </h4>
                <p>
                  Authentication errors now distinguish deactivated-account
                  access blocks from generic failures, with clearer guidance and
                  support-safe metadata for faster recovery.
                </p>
              </div>

              <div
                v-if="isInternalUser"
                class="release-entry"
              >
                <h4>
                  <span class="release-badge purple white--text">Update</span>
                  Admin Feedback and Projects Filter E2E Coverage
                </h4>
                <p>
                  Expanded Playwright coverage for Admin Feedback dashboard
                  management and notification-setting edge handling, plus
                  dedicated Projects filter-flow coverage for filter composition,
                  reset behavior, and view-mode persistence.
                </p>
              </div>

              <div
                v-if="isUserInternalDev"
                class="release-entry"
              >
                <h4>
                  <span class="release-badge darkteal white--text">Update</span>
                  Custom Report Access Remediation Script
                </h4>
                <p>
                  Updated custom-report remediation tooling to improve
                  operational reliability during incident response and
                  entitlement recovery. The access upsert script now includes
                  the correct fallback operator contact and ensures users
                  targeted for custom report remediation are consistently marked
                  as custom report users when permissions are applied.
                </p>
              </div>
            </div>

            <!-- April 2026 -->
            <div
              id="2026-04"
              class="release-section"
            >
              <h3 :class="`mb-1 subtitle-1 font-weight-bold release-month-header ${nearblackTextClass}`">
                April 2026
              </h3>
              <p class="caption coolgray--text mb-4">
                Shipped April 1-30, 2026
              </p>

              <div
                v-if="isInternalUser"
                class="release-entry"
              >
                <h4>
                  <span class="release-badge toasted white--text">Fix</span>
                  Projects Card Load Flicker and Hover Stability
                </h4>
                <p>
                  Fixed a Projects dashboard issue where card actions and text
                  could flicker during initial page load while time-entry
                  details were still resolving. Card rendering now stays stable
                  as async project data hydrates, and project-name hover
                  feedback now uses a color change for clearer, steadier
                  interaction. Header typography and spacing were also refined
                  so client name, project name, and project code align better
                  with card body and footer content. Follow-up hardening now
                  guarantees each card exits the loading state even when
                  time-entry API responses are empty, malformed, or erroring.
                </p>
              </div>

              <div
                v-if="isInternalUser"
                class="release-entry"
              >
                <h4>
                  <span class="release-badge purple white--text">Update</span>
                  Projects Dashboard Real-Time Sync
                </h4>
                <p>
                  The Projects dashboard now listens for live Firestore project
                  updates. Modified projects update in-place, while added or
                  removed projects trigger a list refresh, so dashboard users no
                  longer need to manually refresh to see admin or sync changes.
                </p>
              </div>

              <div
                v-if="isInternalUser"
                class="release-entry"
              >
                <h4>
                  <span class="release-badge primary white--text">New</span>
                  Projects Dashboard Admin Code Rename
                </h4>
                <p>
                  Internal admin users can now rename a project code directly
                  from the Projects dashboard action menu. The flow includes
                  validation, a review confirmation step, Firestore supersede
                  tracking, and API sync so code updates are safer and easier
                  to complete from one place.
                </p>
              </div>

              <div
                v-if="isInternalUser"
                class="release-entry"
              >
                <h4>
                  <span class="release-badge purple white--text">Update</span>
                  Projects Table View: Layout, Navigation, and Action Polish
                </h4>
                <p>
                  The Projects table view received several ergonomic and visual
                  improvements. The Jump to Group button now lives in the same
                  row as the paginator instead of above the table, eliminating
                  the visual gap between navigation controls. The bulk-edit
                  toolbar (select count, Bulk Edit, Archive, Delete) now
                  appears inline in the paginator row when rows are selected,
                  preventing the table from shifting up or down. Right-clicking
                  any row now opens a context menu with View Profile, Edit, and
                  Delete actions. Project type chips and doc links render with
                  correct vertical alignment and font weight.
                </p>
              </div>

              <div
                v-if="isInternalUser"
                class="release-entry"
              >
                <h4>
                  <span class="release-badge toasted white--text">Fix</span>
                  Time View Delegate Entry Visibility
                </h4>
                <p>
                  Fixed an issue where internal managers could miss delegate
                  time entries in the Time view. Delegate linkage now preserves
                  manager mapping during person data normalization, so manager
                  and delegate entries resolve consistently.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge primary white--text">New</span>
                  Settings Available to External Users
                </h4>
                <p>
                  External users now have access to the Settings module to
                  manage workspace preferences. Previously, this section was
                  restricted to internal users only.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge primary white--text">New</span>
                  Settings Tab Deep Linking
                </h4>
                <p>
                  Settings tabs now support URL-based deep linking. Bookmark
                  or share links to specific tabs (e.g.
                  <code>/settings?tab=security</code>) and use browser
                  back/forward to navigate between them.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge primary white--text">New</span>
                  Date and Time Format Preferences
                </h4>
                <p>
                  Date format, time format, and timezone preferences set in
                  Settings &gt; Preferences are now applied when rendering
                  dates and times throughout the app, including the Security
                  tab's last sign-in timestamp and the Data Refresh view.
                  These preferences affect display only and do not change
                  how data is stored.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge purple white--text">Update</span>
                  Docs IA: Feature Overview and Dedicated Guides
                </h4>
                <p>
                  Documentation now separates feature overviews from
                  feature-specific guides. The Features page is now a compact
                  index, and dedicated pages are available for Delivery
                  Reports, Search and Selected Context, and Feedback. QA
                  coverage was also expanded with a dedicated Feedback flow
                  checklist.
                </p>
              </div>

              <div
                v-if="isInternalUser"
                class="release-entry"
              >
                <h4>
                  <span class="release-badge purple white--text">Update</span>
                  Settings Profile and Preferences
                </h4>
                <p>
                  The Profile tab now shows real data from your user record
                  including department (resolved from your team assignment).
                  Preferences auto-populate from your stored theme selection.
                  The Alias tab now includes the full alias form previously
                  only available in the settings modal.
                </p>
              </div>

              <div
                v-if="isInternalUser"
                class="release-entry"
              >
                <h4>
                  <span class="release-badge purple white--text">Update</span>
                  Enterprise View Loading State
                </h4>
                <p>
                  The Admin Enterprise view now shows skeleton placeholders
                  while data loads instead of briefly displaying empty-state
                  messages.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge toasted white--text">Fix</span>
                  Dark Mode Flash Prevention
                </h4>
                <p>
                  Pages no longer flash white before applying dark mode.
                  The theme is resolved synchronously from your saved
                  preference or OS setting before the app renders.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge toasted white--text">Fix</span>
                  Settings and Admin Page Titles
                </h4>
                <p>
                  The browser tab now shows "Settings | iAdOps" and
                  "Admin | iAdOps" instead of "Sign In" when viewing
                  those sections.
                </p>
              </div>

              <div
                v-if="isInternalUser"
                class="release-entry"
              >
                <h4>
                  <span class="release-badge primary white--text">New</span>
                  Password Provider Gating
                </h4>
                <p>
                  The password-change form in Security settings now only
                  appears for enterprises that have password authentication
                  enabled. SSO-only organizations no longer see the form.
                </p>
              </div>

              <div
                v-if="isInternalUser"
                class="release-entry"
              >
                <h4>
                  <span class="release-badge primary white--text">New</span>
                  Admin Users View
                </h4>
                <p>
                  The Admin &gt; Users tab now displays a searchable data
                  table of tenant users with name, email, role, active
                  status, and last sign-in date.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge toasted white--text">Fix</span>
                  Settings Dark Mode Contrast
                </h4>
                <p>
                  Fixed unreadable JSON output in the Debug settings tab
                  when using dark mode. The panel now uses proper dark
                  background and light text colors.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge toasted white--text">Fix</span>
                  Discrepancy and Pacing Custom Fields Crash
                </h4>
                <p>
                  Fixed a render crash on the Discrepancy (and Pacing) views
                  where rows without normalized delivery data caused a
                  <code>TypeError</code> when rendering the Custom Fields
                  column. Affected rows now render correctly with an empty
                  custom-fields value instead of crashing the table.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge toasted white--text">Fix</span>
                  Sign-In Bootstrap Reliability
                </h4>
                <p>
                  Hardened startup authentication by eliminating a race condition, thereby reducing intermittent sign-in
                  failures, especially on preview environments, and improving error accuracy.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge purple white--text">Update</span>
                  Authentication Error Diagnostics
                </h4>
                <p>
                  Improved diagnostics now preserve Firestore permission errors
                  instead of reporting them as missing user records, and add
                  telemetry tags and breadcrumbs for faster issue triage.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge toasted white--text">Fix</span>
                  Access Denied Recovery Guidance and Support Details
                </h4>
                <p>
                  The sign-in error dialog now provides clearer recovery steps
                  when an authenticated account is denied access. For explicit
                  Access Denied responses, the dialog includes the signed-in
                  email and a support-ready details block (error ID, code,
                  and layer) so users can resolve account mismatches faster or
                  share accurate context with support.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge purple white--text">Update</span>
                  Dark Mode Keyboard Shortcut Simplified
                </h4>
                <p>
                  Changed the dark mode toggle shortcut from a two-key chord
                  (Cmd/Ctrl+K then D) to a single-key combo (Cmd/Ctrl+') for
                  improved accessibility and fewer key presses. Press ? to see
                  all keyboard shortcuts.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge primary white--text">New</span>
                  Guided Tours and In-App Feedback in the App Shell
                </h4>
                <p>
                  Users can now launch guided tours directly from the header or
                  with the <code>T</code> keyboard shortcut on supported views.
                  Feedback is also collected in-app through a structured dialog,
                  replacing the old external form flow and keeping support
                  context tied to the current page.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge toasted white--text">Fix</span>
                  Feedback Submission Hardening
                </h4>
                <p>
                  In-app feedback handling now enforces category and message
                  validation on the backend, applies endpoint-specific rate
                  limiting, and stores submissions through server-side writes
                  only. This improves reliability of support intake and helps
                  protect feedback data from direct client-side Firestore access.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge purple white--text">Update</span>
                  Docs Navigation and Release Content Structure
                </h4>
                <p>
                  Docs navigation now uses grouped, collapsible sections to
                  reduce sidebar clutter and improve findability. Release
                  content is now organized under a dedicated Releases section,
                  with Release Summary as the executive quick-link page and
                  Release Notes kept as the chronological source of record.
                  Release Kit communication templates also received improved
                  light-mode readability for better copy-and-share workflows.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge purple white--text">Update</span>
                  In-App Help Redesign Plan and Media Capture Framework
                </h4>
                <p>
                  Added a formal redesign planning package for tours,
                  contextual help, and in-app documentation, including an
                  implementation roadmap, accessibility criteria, IA structure,
                  and a production-ready screenshot and screencast shot list.
                  The plan references legacy documentation visuals and current
                  brand guidance to modernize onboarding without losing visual
                  consistency.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge primary white--text">New</span>
                  Header Help Menu Scaffold and Tour Resume
                </h4>
                <p>
                  Replaced separate header actions with a unified Help menu
                  that centralizes docs search, guided tour controls,
                  page-specific help links, keyboard shortcuts, and feedback.
                  Guided tours now support resume-from-last-step from the
                  header menu on supported views.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge purple white--text">Update</span>
                  Tour System Visual Polish and Copy Refinement
                </h4>
                <p>
                  Guided-tour overlays, cards, controls, and progress states
                  now align with the iAdOps brand palette and typography.
                  Header Help tour actions are easier to scan, and tour/fallback
                  messaging now provides clearer direction to route-specific Page
                  Guide content when tours are unavailable.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge purple white--text">Update</span>
                  Help Menu Audience Labels and Docs Access Points
                </h4>
                <p>
                  Help actions are now ordered and labeled more clearly for
                  internal and external audiences, with page-level guidance,
                  docs browsing, release highlights, shortcuts, and feedback
                  grouped more intentionally. Navigation and user-menu utility
                  links now also expose persistent Docs and What&apos;s New entry
                  points so support content is easier to reach from anywhere in
                  the app shell.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge toasted white--text">Fix</span>
                  Navigation Sidebar Docs Reachability
                </h4>
                <p>
                  Fixed a layout issue in the primary navigation that could
                  prevent users on shorter viewports from scrolling far enough
                  to reach the Docs entry. The drawer now uses a dedicated
                  scroll region with persistent utility shortcuts outside the
                  scroll trap.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge purple white--text">Update</span>
                  Standardized Docs Page Templates
                </h4>
                <p>
                  Core docs pages now share reusable hero, section, and callout
                  blocks to keep onboarding, troubleshooting, release summary,
                  and route-guide layouts visually consistent. This reduces page
                  drift and makes future docs updates easier to apply across the
                  whole docs surface.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge purple white--text">Update</span>
                  Docs Dark-Mode Contrast and Shell Utility Cleanup
                </h4>
                <p>
                  Shared docs hero, section, and callout templates now apply
                  stronger dark-mode contrast for headings, body text, and
                  container backgrounds. Navigation utility links were also
                  simplified by keeping only What&apos;s New in the drawer footer
                  while preserving Docs entry points in the main nav.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge purple white--text">Update</span>
                  Phase 2 Contextual Help Workflows
                </h4>
                <p>
                  Header page-guide copy is now route-aware so users get
                  clearer workflow guidance for the view they are on, and admin
                  projects now map to the correct admin docs guide. Profile
                  menu support links were trimmed to reduce duplication, and
                  Help menu actions now emit telemetry to support Phase 2
                  adoption tracking.
                </p>
              </div>

              <div
                v-if="isUserInternalDev"
                class="release-entry"
              >
                <h4>
                  <span class="release-badge primary white--text">New</span>
                  Help Coverage Audit in Docs QA
                </h4>
                <p>
                  A new Help Coverage Audit page is available under Docs &gt;
                  QA for internal developers. It shows tour and page-guide
                  coverage per route, surfaces which routes still lack a
                  guided tour, and lists all help-menu actions by section and
                  audience to help prioritize Phase 2 onboarding work.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge purple white--text">Update</span>
                  Firestore Multi-Database Deploy Guardrails
                </h4>
                <p>
                  Firestore deployment guidance now uses `iadops-core` as the
                  canonical source for per-database rules and indexes, with
                  automated mirror drift checks in `iadops-project` to reduce
                  accidental mismatches during preview and production workflows.
                  Firestore deploy commands from `iadops-project` are now
                  blocked by default unless an explicit emergency override is
                  provided. High-impact maintenance scripts now default to
                  safe modes and require explicit apply flags before writes,
                  and legacy commit helpers verify mirror sync before staging
                  Firestore rule files.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge toasted white--text">Fix</span>
                  Project Edit Form Stability
                </h4>
                <p>
                  Fixed console errors that appeared when opening the project
                  edit form. The form's auto-suggestions for project codes and
                  duplicate validation now work reliably even when project data
                  is still loading.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge purple white--text">Update</span>
                  Search, Filter, and Auth Test Coverage Realigned
                </h4>
                <p>
                  Updated internal test coverage to match current Search,
                  Filter, Tools store, and Auth contracts, including stronger
                  local-storage isolation for search recents and more accurate
                  defaults validation for route-keyed filters. This improves
                  release confidence and reduces false-positive QA failures
                  during CI verification.
                </p>
              </div>

              <div
                v-if="isInternalUser"
                class="release-entry"
              >
                <h4>
                  <span class="release-badge purple white--text">Update</span>
                  Projects Dashboard UX Design Refinement
                </h4>
                <p>
                  Enhanced the Projects dashboard with design improvements
                  focused on professional, calm aesthetics and clearer visual
                  hierarchy. Project cards now display overdue status with a
                  warning icon and label (instead of negative numbers), progress
                  bars render with softened colors and improved edge-case
                  handling for projects with 0% progress, and card backgrounds
                  use refined rgba-based opacity for a more cohesive appearance
                  in both light and dark modes.
                </p>
              </div>

              <div
                v-if="isInternalUser"
                class="release-entry"
              >
                <h4>
                  <span class="release-badge purple white--text">Update</span>
                  Consolidated App Info into Help Menu
                </h4>
                <p>
                  Simplified the header layout by moving the standalone "Info"
                  button into a new "About" section within the Help menu. The
                  Help menu now includes version, environment, and current view
                  information in a dedicated section at the bottom. This
                  consolidation improves information hierarchy and reduces
                  header button clutter.
                </p>
              </div>

              <div
                v-if="isInternalUser"
                class="release-entry"
              >
                <h4>
                  <span class="release-badge toasted white--text">Fix</span>
                  Help Menu About Section Expansion
                </h4>
                <p>
                  Fixed Help menu behavior so clicking About expands details in
                  place instead of closing the menu. Users can now reliably
                  view version, environment, and current-view metadata without
                  reopening Help.
                </p>
              </div>

              <div
                v-if="isInternalUser"
                class="release-entry"
              >
                <h4>
                  <span class="release-badge toasted white--text">Fix</span>
                  Header Help Menu Contrast Improvements
                </h4>
                <p>
                  Increased contrast for Help menu labels, descriptions,
                  section dividers, hover states, and About metadata values in
                  both light and dark mode to improve readability and visual
                  clarity.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge toasted white--text">Fix</span>
                  Projects Card Footer and Date Output Polish
                </h4>
                <p>
                  Refined Projects card footer alignment, added truncation for
                  long Project Type values to avoid unexpected wrapping, and
                  replaced raw <code>Invalid Date</code> strings with <code>--</code>
                  placeholders for cleaner card output.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge purple white--text">Update</span>
                  Sheets Tab Can Open Full Project Edit Flow
                </h4>
                <p>
                  Admin users can now edit linked Firestore projects directly
                  from the Sheets tab. The action opens the same project edit
                  form used in the Projects tab, so validation, save behavior,
                  and optional API sync remain consistent across both paths.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge purple white--text">Update</span>
                  Sheets Tab Grouping and Shareable Group State
                </h4>
                <p>
                  The Sheets tab now supports grouping rows by Project ID,
                  Billing Code or Client ID, Project Code, Project Type, and
                  Status. Group selections persist in the URL, so grouped views
                  survive refresh and can be shared with teammates.
                </p>
              </div>

              <div
                class="release-entry"
              >
                <h4>
                  <span class="release-badge toasted white--text">Fix</span>
                  Developer Tools Hardened
                </h4>
                <p>
                  The new internal developer testing tools explicitly gate view
                  access to internal admin users, limiting the interface's exposure while
                  preserving internal testing access across environments.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge toasted white--text">Fix</span>
                  Pre-Release Security Audit Updates
                </h4>
                <p>
                  Resolved GitHub Advanced Security findings: enforced HTML
                  escaping in new dev script, reduced potential for sensitive
                  logging in dev environments, and mitigated log injection
                  risks. Maintenance scripts normalized to current ESLint
                  standards.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge toasted white--text">Fix</span>
                  Dropdown Option Row Click Targets
                </h4>
                <p>
                  Fixed deadzones in dropdown menus where option text was
                  clickable only in limited regions. Users can now click either
                  the checkbox icon or the option text in filter dropdowns,
                  project select menus, and grouping controls. This improves
                  selection consistency and interaction speed across filtering
                  and admin interfaces.
                </p>
              </div>

              <div
                v-if="isInternalUser"
                class="release-entry"
              >
                <h4>
                  <span class="release-badge toasted white--text">Fix</span>
                  Sheets Tab Empty Rows No Longer Render
                </h4>
                <p>
                  Fixed an issue where spreadsheet rows without a client value
                  in column A could appear in the Admin Projects Sheets tab.
                  The sheet fetch mapper now skips those rows while
                  preserving original sheet row numbers for review and diff
                  workflows.
                </p>
              </div>

              <div
                v-if="isInternalUser"
                class="release-entry"
              >
                <h4>
                  <span class="release-badge purple white--text">Update</span>
                  API Summary Cards Prioritize Actionable Signals
                </h4>
                <p>
                  The Admin Projects API summary now hides the Deleted and
                  Tests rate cards to reduce false alarms during operational
                  review. Active and linkage metrics remain visible so teams can
                  focus on data-health indicators that require intervention.
                </p>
              </div>

              <!-- Developer/Internal Section -->
              <div
                v-if="isUserInternalDev"
                class="release-entry"
              >
                <h4>
                  <span class="release-badge primary white--text">New</span>
                  Automated Release Notes Generation
                </h4>
                <p>
                  Release notes can now be auto-generated from git commits
                  using <code>npm run release-notes</code>. The generator
                  extracts user-facing descriptions from structured commit
                  messages and groups them by type. Use
                  <code>npm run release-notes:copy</code> to copy to clipboard
                  or <code>npm run release-notes:draft</code> to save a draft
                  file for review before pasting into this page.
                </p>
              </div>

              <div
                v-if="isUserInternalDev"
                class="release-entry"
              >
                <h4>
                  <span class="release-badge primary white--text">New</span>
                  Semantic Versioning Metadata System
                </h4>
                <p>
                  Version bumps are now tracked automatically via metadata.
                  Record each commit's impact with
                  <code>npm run semver:record</code>, review pending changes
                  with <code>npm run semver:metadata</code>, and finalize the
                  release with <code>npm run semver:finalize</code> which
                  auto-detects the correct semver level (major, minor, or
                  patch) from accumulated changes.
                </p>
              </div>

              <div
                v-if="isUserInternalDev"
                class="release-entry"
              >
                <h4>
                  <span class="release-badge purple white--text">Update</span>
                  Deployment Documentation Restructured
                </h4>
                <p>
                  Deployment docs reorganized into focused guides: preview
                  channels, production hosting and backend, and custom domains.
                  A new interactive deploy prompt
                  (<code>deploy.prompt.md</code>) guides deployment decisions
                  with integrated version bumping at each step. See
                  <code>docs/deploy.md</code> for the navigation hub.
                </p>
              </div>

              <div
                v-if="isUserInternalDev"
                class="release-entry"
              >
                <h4>
                  <span class="release-badge purple white--text">Update</span>
                  Release Notes Accuracy Workflow
                </h4>
                <p>
                  Added a release notes accuracy workflow with git-based
                  traceability. When editing release notes for accuracy, a
                  pre-commit hook suggests referencing the original feature
                  commit via <code>Refs:</code> in the commit body, creating a
                  permanent audit trail in git history. Complete workflow
                  guidance is available in
                  <code>docs/release-notes-accuracy-workflow.md</code>.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge primary white--text">New</span>
                  Dashboard Onboarding Card
                </h4>
                <p>
                  Added a role-aware "Start Here" onboarding card on Dashboard
                  with quick actions for common routes and persisted dismiss or
                  reopen behavior to support first-session orientation.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge primary white--text">New</span>
                  Projects Contextual Help Panel
                </h4>
                <p>
                  Added a contextual Help trigger in Projects and a dismissible
                  inline guidance panel near tabs and filters to clarify status
                  navigation, filtering intent, and where to find deeper docs.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge primary white--text">New</span>
                  Search First-Use Primer
                </h4>
                <p>
                  Added a first-use Search primer in the header search dropdown
                  to explain search scope, selected-context behavior, and
                  keyboard access with <code>/</code>. The primer is
                  dismissible, can be reopened, and links to detailed Search
                  guidance in Docs.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge primary white--text">New</span>
                  Time View Onboarding System
                </h4>
                <p>
                  Added comprehensive onboarding for the Time view including a
                  help dialog with tab and layout explanations, dynamic time
                  type descriptions clarifying purpose and requirements, and
                  conditional field hints that guide data entry. Keyboard
                  shortcuts are documented and accessible via <code>?</code>.
                  Deep-links to detailed Time workflow guidance in Docs support
                  first-time orientation.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge primary white--text">New</span>
                  Admin View Onboarding & Safety Confirmations
                </h4>
                <p>
                  Added role-gated help system for Admin view workflows including
                  descriptive tooltips on project merge actions (Separate, Backup,
                  Skip), inline warnings for high-risk operations (sync, link,
                  delete), and deep-links to operational playbooks. Confirmations
                  show impact clarity before destructive actions. Admin-specific
                  help and safety guidelines are hidden from external users,
                  protecting sensitive workflows while supporting internal admin
                  operators.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge primary white--text">New</span>
                  Guided Tours System for First-Time Users
                </h4>
                <p>
                  Launched a comprehensive guided tour system providing interactive
                  walkthroughs for key workflows including Admin Projects overview,
                  Time entry quick-start, and Projects dashboard orientation. Tours
                  feature interactive highlights overlaying target elements, step-by-step
                  tooltips with navigation controls, and full keyboard support (ESC to
                  close, arrow keys to navigate). Tour progress is automatically saved
                  to browser storage, allowing users to resume tours across sessions.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge purple white--text">Update</span>
                  Onboarding Card Professional Design Refresh
                </h4>
                <p>
                  Redesigned the Dashboard onboarding card with a professional,
                  polished appearance: replaced large block buttons with a compact
                  2-column responsive grid (1-column on mobile), moved help text to
                  tooltips for cleaner UI, and optimized spacing and proportions.
                  The card maintains role-aware actions and persistent dismiss/reopen
                  state for improved first-session experience.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge primary white--text">New</span>
                  Admin Project Sheets Tab Status Filter
                </h4>
                <p>
                  Added status filter to the Sheets tab in Project Management, allowing
                  quick filtering of projects by status (Active, On Hold, Archived, etc.)
                  alongside existing Project ID and Project Code filters. Improves workflow
                  efficiency when managing large spreadsheet-sourced project lists.
                </p>
              </div>

              <div
                v-if="isInternalUser"
                class="release-entry"
              >
                <h4>
                  <span class="release-badge purple white--text">Update</span>
                  Admin Project Diff Workflow & Details Dialog Clarity
                </h4>
                <p>
                  The Sheets tab diff control now behaves as a true dropdown with
                  visible mode state (Review, Status, Type) and comparison summaries.
                  Diff mode now applies helpful default column visibility without
                  locking the column picker, so admins can still show or hide any
                  sheet column. The shared View Details dialog now labels Firestore,
                  API, and Sheet source sections more explicitly to reduce review
                  ambiguity during cross-system reconciliation.
                </p>
              </div>

              <div
                v-if="isInternalUser"
                class="release-entry"
              >
                <h4>
                  <span class="release-badge toasted white--text">Fix</span>
                  API Tab Linked-to-Sheet Metric Accuracy
                </h4>
                <p>
                  Fixed an issue where the API tab summary could show
                  <code>Linked to Sheet</code> as zero even when linked records
                  existed. The metric now resolves sheet linkage from the
                  corresponding Firestore project linkage fields used by admin
                  operations.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge primary white--text">New</span>
                  Admin Project Create Flows Brought Into Closer Parity
                </h4>
                <p>
                  The Admin Project create experience now behaves more consistently across Projects and Sheets workflows,
                  with stronger field parity for project types, ownership, links, monthly hours, billing code and project
                  code interactions, and clearer project-link completion behavior.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge toasted white--text">Fix</span>
                  Firestore Write Paths Hardened for Project Operations
                </h4>
                <p>
                  Key Firestore write paths in project merge and configuration repair workflows now use merge semantics
                  to reduce accidental full-document replacement risk and better preserve existing project data.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge purple white--text">Update</span>
                  Project Data Audit Packet Refreshed
                </h4>
                <p>
                  Refreshed the canonical project-data audit documentation and review outputs, and captured separate
                  dry-run remediation proposal iterations to support safer follow-up cleanup of legacy project records.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge purple white--text">Update</span>
                  Admin Project Tab Badges Refined
                </h4>
                <p>
                  Tab count badges now use a more subtle appearance with outlined grey
                  style (x-small size) instead of prominent solid primary color. This
                  maintains visual clarity while reducing visual overwhelm and providing
                  a more refined, professional dashboard appearance.
                </p>
              </div>

              <div
                v-if="isInternalUser"
                class="release-entry"
              >
                <h4>
                  <span class="release-badge toasted white--text">Fix</span>
                  Admin Project Code Now Saved to Firestore
                </h4>
                <p>
                  Editing a project from the Projects tab or linking one from
                  the Sheets tab now correctly persists the
                  <code>projectCode</code> field in the Firestore document
                  body. Previously, the field was used only as the document
                  key and omitted from the payload, making it invisible to
                  queries. The API sync payload was also corrected to include
                  the required <code>person_email</code> field and to only
                  pass <code>project_id</code> when a valid ID is present.
                </p>
              </div>

              <div
                v-if="isInternalUser"
                class="release-entry"
              >
                <h4>
                  <span class="release-badge toasted white--text">Fix</span>
                  Sheets Ingest Safety: Backups and Row-Match Protection
                </h4>
                <p>
                  The sheet ingest planner now creates an automatic mirror backup
                  before any write and protects <code>projectId</code>/<code>projectCode</code>
                  from blank-source overwrites. Protected values are now copied
                  using content-based row matching (instead of positional index)
                  to prevent off-by-one mapping issues after row order shifts.
                  Verbose diff output now highlights real field changes and hides
                  protected-column-only noise by default.
                </p>
              </div>

              <div
                v-if="isInternalUser"
                class="release-entry"
              >
                <h4>
                  <span class="release-badge purple white--text">Update</span>
                  Admin Type/Status/Platform Tables: Shared Toolbar Controls
                </h4>
                <p>
                  Admin Project type, status, and platform tables now share the
                  same toolbar pattern for columns and sheet-label coverage
                  filters. This provides consistent control placement and filter
                  behavior across all three metadata tabs.
                </p>
              </div>

              <div
                v-if="isInternalUser"
                class="release-entry"
              >
                <h4>
                  <span class="release-badge purple white--text">Update</span>
                  Admin Sheets Sync Health Cards Clarified
                </h4>
                <p>
                  The Sheets sync summary now emphasizes health percentages with
                  clearer labels for Status Health, Type Health, Diff Rate, and
                  System Health. This makes cross-system drift easier to spot
                  quickly, especially on larger datasets.
                </p>
              </div>

              <div
                v-if="isInternalUser"
                class="release-entry"
              >
                <h4>
                  <span class="release-badge purple white--text">Update</span>
                  Ingest Report Artifacts Moved to Local-Only Folders
                </h4>
                <p>
                  Routine planner payloads and CSV snapshots now default to
                  <code>reports/generated/</code>, while mirror backups stay in
                  <code>reports/backups/</code>. Both folders are local-only and
                  gitignored so repository history remains focused on curated
                  audit artifacts and source changes.
                </p>
              </div>

              <div
                v-if="isInternalUser"
                class="release-entry"
              >
                <h4>
                  <span class="release-badge toasted white--text">Fix</span>
                  Admin Project Sheets and Edit Sync Hardened
                </h4>
                <p>
                  Strengthened Admin Project create/edit behavior across
                  Firestore, API, and Sheets. Linking API projects from the
                  Sheets tab now writes baseline identity fields into Firestore
                  (<code>projectId</code>, <code>projectCode</code>,
                  <code>projectName</code>, <code>billingCode</code>, and
                  <code>clientId</code>) so records remain visible in Projects.
                  Edit-save now keeps API project name/code/client changes in
                  sync, safely handles project-code renames with supersede
                  tracking, and updates linked sheet project codes. Also fixed
                  client creation from the Sheets create form so newly created
                  billing codes are selectable immediately without reloading.
                  Projects created from Sheets now also appear in the API tab
                  immediately, without requiring a manual Refresh click. The
                  API tab now also supports restoring deleted rows from the
                  action menu for parity with the other admin project tabs.
                </p>
              </div>
              <div class="release-entry">
                <h4>
                  <span class="release-badge toasted white--text">Fix</span>
                  Organization Profile Breadcrumb Navigation
                </h4>
                <p>
                  Fixed the "Organization" breadcrumb on client profile pages.
                  Clicking it now correctly returns to the Organizations list.
                  Previously, the breadcrumb wiped all profile data instead of
                  navigating away, and the client name in the breadcrumb trail
                  appeared blank.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge toasted white--text">Fix</span>
                  Organization Profile Back Navigation Consistency
                </h4>
                <p>
                  Organization profile pages now use the shared breadcrumb
                  component used by project profiles, so the Back button is
                  consistently available on detail pages. Back navigation now
                  also safely falls back to the section dashboard when the page
                  is opened from a direct link without browser history.
                </p>
              </div>

              <div
                v-if="isInternalUser"
                class="release-entry"
              >
                <h4>
                  <span class="release-badge toasted white--text">Fix</span>
                  Admin Security Tenant Guardrails
                </h4>
                <p>
                  Admin Security setup now validates tenant identifiers before
                  loading tenant-scoped data. Invalid or malformed tenant IDs
                  are rejected early, preventing cross-tenant lookup failures
                  and reducing noisy initialization errors in the Admin
                  Security workflow.
                </p>
              </div>

              <div
                v-if="isUserInternalDev"
                class="release-entry"
              >
                <h4>
                  <span class="release-badge primary white--text">New</span>
                  Localhost API Stubs Auto-Activation
                </h4>
                <p>
                  Local development now automatically enables API stubs when
                  running on <code>localhost</code>. This removes manual setup
                  friction, improves deterministic local testing, and reduces
                  accidental calls to live services during development sessions.
                </p>
              </div>

              <div
                v-if="isUserInternalDev"
                class="release-entry"
              >
                <h4>
                  <span class="release-badge toasted white--text">Fix</span>
                  E2E Auth Bypass Security Hardened
                </h4>
                <p>
                  Added four security guards to the Playwright E2E custom-token
                  auth path. <code>generate-test-token.mjs</code> now blocks
                  execution in production unless
                  <code>ALLOW_E2E_IN_PRODUCTION=true</code> is explicitly set,
                  rejects <code>iam.gserviceaccount.com</code> service accounts
                  as test identities, and requires
                  <code>ALLOW_API_UID_FALLBACK=true</code> before the legacy
                  <code>API_UID</code> fallback is used. In the app,
                  <code>window.__e2eSignIn</code> is now gated by a new
                  <code>canExposeE2ECustomAuth()</code> helper that restricts
                  exposure to <code>localhost</code> and preview-channel hosts
                  only. <code>API_UID</code> is marked as opt-in legacy — the
                  preferred path is <code>TEST_UID</code> or
                  <code>TEST_EMAIL</code>.
                </p>
              </div>

              <div
                v-if="isUserInternalDev"
                class="release-entry"
              >
                <h4>
                  <span class="release-badge toasted white--text">Fix</span>
                  Font Awesome CI Auth and NPM Registry Config
                </h4>
                <p>
                  Added a project-level <code>.npmrc</code> that routes
                  <code>@fortawesome</code> and <code>@awesome.me</code>
                  packages to the Font Awesome private registry via
                  <code>${FONTAWESOME_NPM_AUTH_TOKEN}</code> variable
                  substitution. The token is now injected from GitHub Actions
                  secrets in both <code>test.yml</code> and
                  <code>preview-e2e.yml</code>, resolving the E401
                  authentication failure that was blocking CI Fast Checks on
                  <code>npm ci</code> runs. Add
                  <code>FONTAWESOME_NPM_AUTH_TOKEN</code> as a repository
                  Actions secret if not already set.
                </p>
              </div>

              <div
                v-if="isUserInternalDev"
                class="release-entry"
              >
                <h4>
                  <span class="release-badge purple white--text">Update</span>
                  GitHub Actions Runtime Upgraded to Node.js 22
                </h4>
                <p>
                  Upgraded <code>actions/checkout</code>,
                  <code>actions/setup-node</code>, and
                  <code>actions/upload-artifact</code> from v4 to v5 across
                  <code>test.yml</code> and <code>preview-e2e.yml</code>.
                  v5 actions use a Node.js 22 internal runtime, eliminating
                  the Node.js 20 deprecation warnings that appeared in all
                  CI runs.
                </p>
              </div>

              <div
                v-if="isUserInternalDev"
                class="release-entry"
              >
                <h4>
                  <span class="release-badge primary white--text">New</span>
                  CodeQL Analysis Scoped to <code>src/</code>
                </h4>
                <p>
                  Added an explicit <code>.github/workflows/codeql.yml</code>
                  and <code>.github/codeql/codeql-config.yml</code> that
                  restrict CodeQL's analysis to the <code>src/</code>
                  directory. This eliminates ~40 false-positive high-severity
                  alerts sourced from transitive Vue CLI, webpack 4, and
                  Babel dependencies that cannot be resolved without a full
                  Vue 2 &rarr; Vue 3 + Vite migration. The
                  <code>security-extended</code> query suite runs on
                  pushes to <code>rc/next</code> and feature branches, and
                  on a weekly schedule.
                </p>
              </div>
            </div>

            <!-- March 2026 -->
            <div
              id="2026-03"
              class="release-section"
            >
              <h3 :class="`mb-1 subtitle-1 font-weight-bold release-month-header ${nearblackTextClass}`">
                March 2026
              </h3>
              <p class="caption coolgray--text mb-4">
                Shipped March 1 – 31, 2026
              </p>

              <div class="release-entry">
                <h4>
                  <span class="release-badge primary white--text">New</span>
                  Project Owner and Billing Code Admin Controls
                </h4>
                <p>
                  Internal admin tooling now includes a Project Owner field and
                  expanded billing code filtering to improve project ownership
                  visibility and day-to-day portfolio management.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge toasted white--text">Fix</span>
                  API Project Selection in Admin Forms
                </h4>
                <p>
                  Replaced the API sync checkbox flow with a structured API
                  project dropdown to reduce input ambiguity and improve admin
                  project mapping accuracy.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge toasted white--text">Fix</span>
                  Firestore Sparse Project Record Repair
                </h4>
                <p>
                  Repaired sparse Firestore project documents to restore missing
                  fields and stabilize downstream filtering, tab counts, and
                  admin edit consistency.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge purple white--text">Update</span>
                  Project Data Review and Migration Tooling
                </h4>
                <p>
                  Added project audit, migration, and data review packet tools
                  to support safer bulk validation and remediation workflows
                  across API and Firestore project datasets.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge purple white--text">Update</span>
                  Project Tab Visual Consistency
                </h4>
                <p>
                  Normalized project tab chip color behavior for more consistent
                  visual state cues across active, archived, and deleted views.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge toasted white--text">Fix</span>
                  Project Form Reliability
                </h4>
                <p>
                  Fixed admin project edit forms so Save now enables when billing
                  code or project code changes, project code validation allows
                  unchanged values, and duplicate-code errors identify the active
                  conflicting project.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge primary white--text">New</span>
                  Project Links and Multi-Type Support
                </h4>
                <p>
                  Admin project create and edit forms now support multiple project
                  types and project link entries for assets like Google Drive
                  documents. Project type display was updated to render multiple
                  type chips while remaining backward compatible with existing
                  single-type records.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge purple white--text">Update</span>
                  Admin Projects: Sync &amp; Sheets Controls
                </h4>
                <p>
                  The admin projects edit dialog now includes a Sync to API
                  toggle, and the Sheets tab uses the clearer label Account
                  Manager instead of AM.
                </p>
              </div>

              <!-- All Users -->
              <div class="release-entry">
                <h4>
                  <span class="release-badge primary white--text">New</span>
                  Enhanced Project Search
                </h4>
                <p>
                  Projects now appear in the global search bar with improved
                  matching across project codes, names, and client associations.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge toasted white--text">Fix</span>
                  Delivery View Navigation
                </h4>
                <p>
                  Fixed Pacing and Discrepancy search results that previously
                  navigated to broken URLs. Both views now use query parameters
                  for reliable deep-linking and browser history support.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge toasted white--text">Fix</span>
                  Project Modal Validation
                </h4>
                <p>
                  Fixed validation feedback for required fields and improved
                  error messaging when project creation fails.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge toasted white--text">Fix</span>
                  Pacing Dashboard for External Users
                </h4>
                <p>
                  Fixed an issue where external users saw dashboard cards
                  with 0 results even when line item data existed. Cards
                  now display accurate counts for All Results, At Risk,
                  Missing Creatives, and Zero Impressions.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge toasted white--text">Fix</span>
                  Pacing Tab Content Sync
                </h4>
                <p>
                  Resolved a bug where switching between networks could
                  cause tab counts and table content to get out of sync.
                  The correct data now displays immediately after navigation.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge purple white--text">Update</span>
                  At Risk Tab Tooltip
                </h4>
                <p>
                  The At Risk tab now includes a tooltip explaining its
                  criteria: delivering line items with an Order Status
                  Index (OSI) between 0 and 99. Hover over the tab label
                  to see the explanation.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge primary white--text">New</span>
                  Inline Documentation
                </h4>
                <p>
                  The Documentation section now displays feature guides, filter
                  references, and saved filter definitions directly in the app.
                  No more PDF downloads required. Content includes Pacing and
                  Discrepancy filter breakdowns, saved filter criteria (At Risk,
                  Stuck in Paused, Missing Creatives, etc.), and OSI explanations.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge toasted white--text">Fix</span>
                  Sign-In Page Accessibility
                </h4>
                <p>
                  Added a Back button to the provider selection view and
                  fixed contrast issues on the sign-in page. The auth
                  screen keeps the same dark gradient background in both
                  light and dark themes, so the Back button and logo now
                  stay readable regardless of theme state.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge purple white--text">Update</span>
                  Dark Mode Persistence
                </h4>
                <p>
                  Dark mode preference is now cached locally and applied
                  before Vue mounts, so the correct theme renders on page
                  load without a brief white flash.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge toasted white--text">Fix</span>
                  Chip Contrast on Pacing Dashboard
                </h4>
                <p>
                  Quick Metrics chips on Pacing dashboard cards now display
                  white text on colored backgrounds for better readability
                  and WCAG AA contrast compliance.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge toasted white--text">Fix</span>
                  Breadcrumb Navigation on Reports
                </h4>
                <p>
                  Clicking the "Pacing" or "Discrepancy" breadcrumb on
                  report pages now correctly returns to the dashboard
                  instead of appearing unclickable.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge toasted white--text">Fix</span>
                  Discrepancy Dashboard Card Navigation
                </h4>
                <p>
                  Fixed an issue where clicking a network card on the
                  Discrepancy dashboard briefly changed the URL then
                  reverted to the dashboard. Cards now navigate to the
                  report view reliably.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge toasted white--text">Fix</span>
                  Table Cell Formatting
                </h4>
                <p>
                  Pacing and Discrepancy report tables now display
                  formatted values (dates, percentages, currency) instead
                  of raw data. Column formatting defined in the report
                  configuration is now applied automatically.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge toasted white--text">Fix</span>
                  Time View Loading
                </h4>
                <p>
                  Resolved a timeout error that caused the Time view
                  to take over 5 seconds to load. The view now loads
                  promptly with a module registration fallback.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge toasted white--text">Fix</span>
                  Projects Dashboard Table UX
                </h4>
                <p>
                  Improved Projects table behavior on desktop and mobile:
                  toolbar action buttons now follow outlined styling,
                  card/table mode syncs with URL query parameters,
                  grouped headers have stronger accessibility semantics,
                  sorting by Type reorders rows correctly, and small-screen
                  layouts keep tab arrows and Add Project actions visible.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge toasted white--text">Fix</span>
                  Projects Tab Filtering &amp; Search
                </h4>
                <p>
                  Archived and deleted projects are now excluded from all tabs
                  except their dedicated Archived and Deleted tabs. Previously,
                  searching for an archived project (like AFAR) incorrectly
                  returned results in the Active tab. Tab chips for Archived
                  and Deleted now display in red for visual distinction.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge purple white--text">Update</span>
                  Project Filter Multi-Select
                </h4>
                <p>
                  Filter dropdowns for Client, Status, and Type now support
                  multi-select, allowing you to filter by multiple values
                  at once (e.g., show both Active and On Hold projects).
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge toasted white--text">Fix</span>
                  Project Card Hover Stability
                </h4>
                <p>
                  Fixed a visual glitch where project cards would shimmy
                  or shake slightly on hover. Cards now display a subtle
                  shadow effect without any movement artifacts.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge toasted white--text">Fix</span>
                  Description Column Toggle
                </h4>
                <p>
                  The Description column on project cards now correctly
                  responds to the Column Picker setting. Previously, the
                  description was always hidden regardless of preferences.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge toasted white--text">Fix</span>
                  Project Card Header Spacing
                </h4>
                <p>
                  Tightened the vertical spacing between client names and
                  project names on project cards so the header reads as a
                  single grouped title instead of two loosely separated rows.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge toasted white--text">Fix</span>
                  Project Tab Count Stability
                </h4>
                <p>
                  Fixed an issue where project tab counts would shift after
                  initial page load (e.g., Archive: 7&rarr;4, Active: 13&rarr;12).
                  Boolean flags for archived, deleted, and test projects are
                  now normalized consistently during data loading, preventing
                  count fluctuations when switching between API and Firestore
                  data sources.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge primary white--text">New</span>
                  Breadcrumb Back Navigation
                </h4>
                <p>
                  Profile pages now include a back arrow button in the
                  breadcrumb bar for quick navigation to the previous page.
                  Project profiles also display a 3-level breadcrumb path
                  (Projects &gt; Client Name &gt; Project Name) when the
                  client is loaded.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge purple white--text">Update</span>
                  Project Card Layout
                </h4>
                <p>
                  Project cards now place the project type in the card
                  footer alongside the status chip, and the context menu
                  has moved next to the client name for a cleaner header.
                  A new Deleted tab lets you browse soft-deleted projects.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge purple white--text">Update</span>
                  Grouped Card Headers
                </h4>
                <p>
                  Grouped card sections in Projects now use a calmer header
                  treatment with a framed surface, clearer collapse affordance,
                  and a polished project-count badge so grouped cards feel more
                  grounded and intentional.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge purple white--text">Update</span>
                  Grouped Table Headers
                </h4>
                <p>
                  Grouped headers in table view now match the refined Projects
                  card aesthetic more closely, with the same grounded visual
                  treatment and tighter spacing between collapsed groups.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge purple white--text">Update</span>
                  Projects Toolbar Responsiveness
                </h4>
                <p>
                  Projects toolbar labels now collapse at the same breakpoint
                  across Filter, Group, Sort, Columns, Export, and other action
                  buttons. The search field is also slightly narrower with a
                  shorter placeholder for a cleaner controls row.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge toasted white--text">Fix</span>
                  Projects Toolbar Menu Layering
                </h4>
                <p>
                  Projects toolbar menus now open above grouped card and table
                  headers consistently, so Filter, Group, Sort, and Columns stay
                  fully readable while grouped sections remain visible beneath.
                </p>
              </div>

              <!-- Internal Users -->
              <div v-if="isInternalUser">
                <v-divider class="my-4" />
                <p class="caption font-weight-bold text-uppercase bluegray--text mb-3">
                  Internal
                </p>

                <div class="release-entry">
                  <h4>
                    <span class="release-badge primary white--text">New</span>
                    Project Form Dropdown Enhancements
                  </h4>
                  <p>
                    Project Type, Status, and Platforms dropdowns now load options
                    from Firestore with proper loading indicators. The "Create New
                    Project Type" option opens an inline form for on-the-fly additions.
                  </p>
                </div>

                <div class="release-entry">
                  <h4>
                    <span class="release-badge primary white--text">New</span>
                    Smart Project Form
                  </h4>
                  <p>
                    The project name field now auto-parses "CODE ProjectName" input
                    to split the project code and name automatically. Billing codes
                    display as a chip preview next to the project code, and duplicate
                    code detection prevents conflicts before saving.
                  </p>
                </div>

                <div class="release-entry">
                  <h4>
                    <span class="release-badge purple white--text">Update</span>
                    Project Cards &amp; Profiles
                  </h4>
                  <p>
                    Project cards now show the client name with a link to the
                    organization profile, and the status chip has moved to
                    the card footer for a cleaner header layout. Context menus
                    use Font Awesome icons with a compact list style. The project
                    profile view displays client info, time entry summaries, and
                    a richer detail section.
                  </p>
                </div>

                <div class="release-entry">
                  <h4>
                    <span class="release-badge toasted white--text">Fix</span>
                    Compact Client Assignment Form
                  </h4>
                  <p>
                    The compact assign-client form now updates the project
                    code when the billing code changes, matching the
                    behavior of the full edit form. Fixed a flash issue
                    where the full edit form briefly appeared during save.
                  </p>
                </div>

                <div class="release-entry">
                  <h4>
                    <span class="release-badge toasted white--text">Fix</span>
                    Admin Project Table Reactivity
                  </h4>
                  <p>
                    Editing a project's status or type in the admin
                    interface now updates the table row immediately
                    without requiring a page refresh.
                  </p>
                </div>

                <div class="release-entry">
                  <h4>
                    <span class="release-badge purple white--text">Update</span>
                    Admin Project Issues Review
                  </h4>
                  <p>
                    The Admin Projects table now includes an Issues column with
                    per-project issue counts and severity chips. Clicking a chip
                    opens a detailed dialog with manual-review items and
                    recommended remediations. Export menus in Projects, API,
                    and Sheets tabs now include JSON alongside CSV and XLSX,
                    and the Issues dialog can export a project-specific JSON
                    payload for data review.
                  </p>
                </div>

                <div class="release-entry">
                  <h4>
                    <span class="release-badge toasted white--text">Fix</span>
                    Admin Project Issue Summary Counts
                  </h4>
                  <p>
                    The Projects-tab issue summary chip now updates from the
                    currently visible Admin Projects rows, so the count stays in
                    sync with active filters and search text instead of showing a
                    stale total.
                  </p>
                </div>

                <div class="release-entry">
                  <h4>
                    <span class="release-badge primary white--text">New</span>
                    Admin Feedback Dashboard
                  </h4>
                  <p>
                    Internal admins can now review in-app feedback from a
                    dedicated <code>/admin/feedback</code> tab, update status,
                    add internal notes, export filtered rows, and manage
                    immediate email recipients for new submissions. Notification
                    delivery is fail-safe when email auth is not configured, so
                    user feedback intake stays reliable while broader routing is
                    phased in.
                  </p>
                </div>
              </div>

              <!-- Developer Users -->
              <div v-if="isUserInternalDev">
                <v-divider class="my-4" />
                <p class="caption font-weight-bold text-uppercase bluegray--text mb-3">
                  Developer
                </p>

                <div class="release-entry">
                  <h4>
                    <span class="release-badge darkteal white--text">New</span>
                    QA Checklists in Docs
                  </h4>
                  <p>
                    Internal developers can now access QA test checklists at
                    <code>/docs/test</code> for structured feature testing.
                    Checklists include progress tracking, copy-to-clipboard
                    markdown export, and remaining items tables.
                  </p>
                </div>

                <div class="release-entry">
                  <h4>
                    <span class="release-badge darkteal white--text">Update</span>
                    Docs Search Options
                  </h4>
                  <p>
                    The docs search bar now includes QA Checklists for users
                    with <code>isUserInternalDev</code> permissions.
                  </p>
                </div>

                <div class="release-entry">
                  <h4>
                    <span class="release-badge darkteal white--text">Update</span>
                    QA Checklist Readability Refresh
                  </h4>
                  <p>
                    The QA checklist experience received visual improvements for
                    readability and scannability, including clearer summary and
                    validation cards, improved note and table legibility, and
                    updated preview environment links to <code>next.iadops.com</code>
                    for active checklist suites.
                  </p>
                </div>

                <div class="release-entry">
                  <h4>
                    <span class="release-badge darkteal white--text">New</span>
                    View as External Toggle
                  </h4>
                  <p>
                    Internal users can now preview the documentation exactly as
                    external users see it using the "View as External" toggle
                    in the docs header. Internal-only content (Time Entry,
                    Projects, People views) is hidden when toggled.
                  </p>
                </div>

                <div class="release-entry">
                  <h4>
                    <span class="release-badge darkteal white--text">New</span>
                    Pacing QA Checklist
                  </h4>
                  <p>
                    Added a Pacing view test checklist at
                    <code>/docs/test</code> covering dashboard, report
                    tabs, navigation, and API resilience scenarios for
                    both internal and external users (28 test items).
                  </p>
                </div>

                <div class="release-entry">
                  <h4>
                    <span class="release-badge darkteal white--text">Update</span>
                    Keyboard Shortcuts
                  </h4>
                  <p>
                    Dark mode now uses the chord <code>Cmd/Ctrl+K, Cmd/Ctrl+D</code>
                    to avoid conflicts with browser shortcuts.
                    <code>Cmd/Ctrl+,</code> toggles Settings on and off.
                    Press <code>?</code> to see all shortcuts in a
                    redesigned table with Action, Shortcut, and
                    Description columns.
                  </p>
                </div>

                <div class="release-entry">
                  <h4>
                    <span class="release-badge darkteal white--text">Fix</span>
                    Header Info Dialog
                  </h4>
                  <p>
                    The info icon dialog now displays its full body
                    content, including app version, environment,
                    and current view.
                  </p>
                </div>

                <div class="release-entry">
                  <h4>
                    <span class="release-badge darkteal white--text">New</span>
                    Assign Clients Pre-Selection
                  </h4>
                  <p>
                    The Assign Clients dialog on the Pacing dashboard now
                    shows currently assigned clients as pre-selected,
                    making it easier to review and modify assignments.
                  </p>
                </div>

                <div class="release-entry">
                  <h4>
                    <span class="release-badge darkteal white--text">Update</span>
                    Project Code Normalization
                  </h4>
                  <p>
                    Standardized 94 project codes to the
                    <code>{ALPHA}{YEAR}_P{YEAR}-{SEQ}</code> format via
                    automated script with full audit trail
                    (<code>previousProjectCode</code>, <code>normalizedAt</code>).
                    Added "Unknown" status and type documents so every project
                    has an explicit status.
                  </p>
                </div>

                <div class="release-entry">
                  <h4>
                    <span class="release-badge darkteal white--text">New</span>
                    Project Data Review Packet
                  </h4>
                  <p>
                    Added a generated review bundle for Firestore project-data
                    triage, including categorized JSON outputs for auto-fixable
                    and manual-review issues. New handoff and playbook docs
                    provide a consistent review workflow, decision capture
                    template, and regeneration commands for repeatable audits.
                  </p>
                </div>
              </div>
            </div>

            <!-- February 2026 -->
            <div
              id="2026-02"
              class="release-section"
            >
              <h3 :class="`mb-1 subtitle-1 font-weight-bold release-month-header ${nearblackTextClass}`">
                February 2026
              </h3>
              <p class="caption coolgray--text mb-4">
                Shipped February 17 – 25, 2026
              </p>

              <!-- All Users -->
              <div class="release-entry">
                <h4>
                  <span class="release-badge purple white--text">Update</span>
                  Standardized Toolbars &amp; Data Patterns
                </h4>
                <p>
                  Views now share a consistent toolbar layout with group, filter,
                  and export controls — making it easier to find the tools you need,
                  no matter which view you're in.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge purple white--text">Update</span>
                  Status Color Redesign
                </h4>
                <p>
                  Project status chips now use color psychology for at-a-glance recognition:
                  green for success, blue for active work, purple for planning, and red for alerts.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge toasted white--text">Fix</span>
                  Search Bar Selection
                </h4>
                <p>
                  Resolved a race condition where rapid search selections could
                  pick the wrong item.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge toasted white--text">Fix</span>
                  Dashboard Card Navigation
                </h4>
                <p>
                  Dashboard cards now use tab query parameters for reliable navigation,
                  ensuring you land on the right view every time.
                </p>
              </div>

              <!-- Internal Users -->
              <div v-if="isInternalUser">
                <v-divider class="my-4" />
                <p class="caption font-weight-bold text-uppercase bluegray--text mb-3">
                  Internal
                </p>

                <div class="release-entry">
                  <h4>
                    <span class="release-badge primary white--text">New</span>
                    Projects Dashboard
                  </h4>
                  <p>
                    A full project management view with search, filtering, grouping,
                    color-coded status chips, and breadcrumb navigation to individual
                    project profiles. Navigate directly to a project via URL.
                  </p>
                </div>

                <div class="release-entry">
                  <h4>
                    <span class="release-badge purple white--text">Update</span>
                    Project Modal Redesign
                  </h4>
                  <p>
                    Cleaner form layout with side-by-side fields, auto-derived billing codes,
                    collapsible advanced options, populated dropdowns with loading states,
                    and field validation for project codes and hours.
                  </p>
                </div>

                <div class="release-entry">
                  <h4>
                    <span class="release-badge primary white--text">New</span>
                    CSV &amp; XLSX Export for Projects &amp; Time
                  </h4>
                  <p>
                    Export project and time entry data directly from the toolbar
                    to CSV or XLSX for billing reconciliation, stakeholder reporting,
                    or Google Drive integration.
                  </p>
                </div>

                <div class="release-entry">
                  <h4>
                    <span class="release-badge toasted white--text">Fix</span>
                    Time Entry Form
                  </h4>
                  <p>
                    Resolved empty time type dropdown and multiple form issues.
                    Restored full export functionality to the Time view.
                  </p>
                </div>

                <div class="release-entry">
                  <h4>
                    <span class="release-badge toasted white--text">Fix</span>
                    Project Profile Navigation
                  </h4>
                  <p>
                    Direct URL navigation to project profiles now works correctly.
                    Breadcrumb back-navigation reloads the dashboard list.
                  </p>
                </div>

                <div class="release-entry">
                  <h4>
                    <span class="release-badge toasted white--text">Fix</span>
                    Navigation &amp; Page Titles
                  </h4>
                  <p>
                    Fixed page title timing and resolved several routing issues,
                    including search bar generating dashboard options for all self-managed views.
                  </p>
                </div>
              </div>

              <!-- Developer Users -->
              <div v-if="isUserInternalDev">
                <v-divider class="my-4" />
                <p class="caption font-weight-bold text-uppercase bluegray--text mb-3">
                  Developer
                </p>

                <div class="release-entry">
                  <h4>
                    <span class="release-badge darkteal white--text">New</span>
                    Admin Firestore Projects Tab
                  </h4>
                  <p>
                    Inspect raw Firestore project data and export entire collections
                    from the Admin Project Management view — useful for debugging,
                    analysis, and data migration.
                  </p>
                </div>

                <div class="release-entry">
                  <h4>
                    <span class="release-badge darkteal white--text">New</span>
                    Firestore Collection Export CLI
                  </h4>
                  <p>
                    <code>node scripts/firestore-export.js --collections=projects</code>
                    — a command-line tool supporting selective export, multi-database projects,
                    compact output, and automatic Timestamp conversion.
                  </p>
                </div>

                <div class="release-entry">
                  <h4>
                    <span class="release-badge darkteal white--text">Update</span>
                    Error Handling &amp; Observability
                  </h4>
                  <p>
                    Project modal async errors are now caught and logged via <code>$log.error</code>
                    instead of becoming unhandled Promise rejections invisible to Sentry.
                    Firestore <code>set</code> operations now return the document for chaining.
                  </p>
                </div>
              </div>
            </div>

            <div
              v-if="isInternalUser"
              id="2025-11"
            >
              <h3 :class="`mb-1 subtitle-1 font-weight-bold release-month-header ${nearblackTextClass}`">
                November 2025
              </h3>
              <p class="caption coolgray--text mb-4">
                Shipped November 3 – 24, 2025
              </p>

              <!-- All Users -->
              <div class="release-entry">
                <h4>
                  <span class="release-badge primary white--text">New</span>
                  Person Profile View
                </h4>
                <p>
                  Navigate directly to a user's profile via deep links.
                  Profiles include card-based navigation, dark mode support,
                  and enhanced styling.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge primary white--text">New</span>
                  Global Search Shortcut
                </h4>
                <p>
                  Press <code>/</code> anywhere in the app to instantly focus the search bar.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge primary white--text">New</span>
                  Dynamic Favicon
                </h4>
                <p>
                  The browser tab icon now adapts to your dark mode preference automatically.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge purple white--text">Update</span>
                  Table Pagination Options
                </h4>
                <p>
                  New 10, 25, and 50 rows-per-page options give you finer control
                  over table data density.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge purple white--text">Update</span>
                  Time Entry Date Range
                </h4>
                <p>
                  The Time Entry view's default date range now spans three months —
                  from the beginning of last month to the end of next month —
                  giving you more context without sacrificing performance.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge purple white--text">Update</span>
                  Sign-In Privacy Policy
                </h4>
                <p>
                  The sign-in page now links to the privacy policy instead of a registration form.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge toasted white--text">Fix</span>
                  Navigation &amp; Routing
                </h4>
                <p>
                  Prevented duplicate navigations, fixed page title timing,
                  added query-aware navigation, and optimized route initialization.
                </p>
              </div>

              <div class="release-entry">
                <h4>
                  <span class="release-badge toasted white--text">Fix</span>
                  Dashboard Pacing Severity
                </h4>
                <p>
                  Corrected pacing category severity levels on the Dashboard for accurate at-a-glance status.
                </p>
              </div>

              <!-- Internal Users -->
              <div v-if="isInternalUser">
                <v-divider class="my-4" />
                <p class="caption font-weight-bold text-uppercase bluegray--text mb-3">
                  Internal
                </p>

                <div class="release-entry">
                  <h4>
                    <span class="release-badge primary white--text">New</span>
                    Organization Quick Stats Banner
                  </h4>
                  <p>
                    A summary banner at the top of the Organization view showing
                    key statistics at a glance.
                  </p>
                </div>

                <div class="release-entry">
                  <h4>
                    <span class="release-badge primary white--text">New</span>
                    Inactive Status Control for Organizations
                  </h4>
                  <p>
                    Mark organizations as inactive directly from the Organization view.
                  </p>
                </div>

                <div class="release-entry">
                  <h4>
                    <span class="release-badge purple white--text">Update</span>
                    Architecture Migration
                  </h4>
                  <p>
                    Core views migrated from global Vuex stores to component-level
                    data management for better encapsulation and performance.
                  </p>
                </div>

                <div class="release-entry">
                  <h4>
                    <span class="release-badge purple white--text">Update</span>
                    Auth Views Dark Mode
                  </h4>
                  <p>
                    Sign-in and authentication screens now fully support dark mode.
                  </p>
                </div>

                <div class="release-entry">
                  <h4>
                    <span class="release-badge purple white--text">Update</span>
                    Font Awesome v7 Upgrade
                  </h4>
                  <p>
                    Icons upgraded from v6.7.1 to v7.1.0 with optimized imports,
                    corrected prefixes, and fallback error handling for navigation.
                  </p>
                </div>

                <div class="release-entry">
                  <h4>
                    <span class="release-badge purple white--text">Update</span>
                    Organization &amp; Delivery View Refactoring
                  </h4>
                  <p>
                    Completed filtering and navigation enhancements for Organization view.
                    Pacing and Discrepancy views received full refactoring with sticky headers,
                    a new pagination component, and visual enhancements.
                  </p>
                </div>

                <div class="release-entry">
                  <h4>
                    <span class="release-badge primary white--text">New</span>
                    GroupToolbar Component
                  </h4>
                  <p>
                    A reusable toolbar component with pending state, standardized button
                    styling, and local mode support for views that manage their own data.
                  </p>
                </div>
              </div>

              <!-- Developer Users -->
              <div v-if="isUserInternalDev">
                <v-divider class="my-4" />
                <p class="caption font-weight-bold text-uppercase bluegray--text mb-3">
                  Developer
                </p>

                <div class="release-entry">
                  <h4>
                    <span class="release-badge darkteal white--text">Fix</span>
                    Webpack Configuration for Tableau Embedding API
                  </h4>
                  <p>
                    Resolved build failures from missing @tableau/embedding-api v3.13.0 internal
                    dependencies via <code>webpack.IgnorePlugin</code>.
                  </p>
                </div>

                <div class="release-entry">
                  <h4>
                    <span class="release-badge darkteal white--text">Fix</span>
                    Firebase App Check
                  </h4>
                  <p>
                    Improved debug token handling and moved App Check initialization
                    to <code>main.js</code> for proper setup.
                  </p>
                </div>
              </div>
            </div>

            <div
              id="2025-06"
            >
              <h3
                :class="`mb-3 subtitle-1 font-weight-bold ${nearblackTextClass}`"
              >
                June 2025
              </h3>

              <!-- Enhance user experience for admins and internals ons

              - Enhances Admin module's profile view
              - Matures the Admin Enterprise view
              - Introduces the Admin Logs view
              - Exposes dark mode as beta feature
              - Moves toward Release Notes on Docs page
              - Adds default user profile image
              - Modernizes navigation icons for dark mode
              - Resizes navigation icons to improve their visiblity
              - Rebrands the navigation sidebar with new logo
              - Updates navigation sidebar's copyright text in the footer
              - Fixes ServiceWorker registration error- Enhances user permissions and settings available
              - Exposes new permissions and settings properties in the Person form
              - Reorganizes fields in the Person form for better UX and error handling -->
              <div>
                <h4>
                  <span class="purple white--text px-1 mr-1">Update</span> Admin Module Enhancements
                </h4>

                <p>
                  The Admin module has been enhanced with new views, including a dedicated view for users to manage their profile.
                </p>
              </div>

              <div>
                <h4>
                  <span class="purple white--text px-1 mr-1">Update</span> Admin Enterprise
                </h4>

                <p>
                  The Admin Enterprise view has matured, providing a more comprehensive overview of the organization's operations. Permissioned users can now access this view to manage enterprise-level settings and configurations.
                </p>

                <p>
                  Additionally, a default user profile image has been added. Navigation icons have been modernized and resized for better visibility
                </p>
              </div>

              <div>
                <h4>
                  <span class="primary white--text px-1 mr-1">New</span> Admin Logs View
                </h4>

                <p>
                  The Admin Logs view provides a detailed log of administrative actions and changes made within the application.
                  This view is accessible to permissioned users and helps in tracking changes and maintaining accountability.
                </p>
              </div>

              <div>
                <h4>
                  <span class="purple white--text px-1 mr-1">Update</span> User Permissions and Settings
                </h4>

                <p>
                  Admin views expose new permissions and settings properties in their tables and user forms.
                  With the forms' growth, fields have been grouped for better user experience and error handling.
                </p>
              </div>

              <div>
                <h4>
                  <span class="primary white--text px-1 mr-1">New</span> Dark Mode (Beta)
                </h4>

                <p>
                  Dark Mode is now available in iAdOps. Users can toggle Dark Mode on or off
                  in the Settings view, which can be accessed by clicking on their profile in the sidebar.
                  This feature is currently in Beta. If you encounter any issues or have feedback,
                  please <a
                    href="mailto:tech@adops.com"
                  >contact our support team</a>.
                </p>
              </div>

              <div>
                <h4>
                  <span class="primary white--text px-1 mr-1">New</span> Rebranding to adops.com
                </h4>

                <p>
                  OAO has rebranded to adops.com. Accordingly, the application's logo and copyright text has been updated to reflect this change.
                </p>
              </div>

              <div>
                <h4>
                  <span class="primary white--text px-1 mr-1">New</span> Security Center
                </h4>

                <p>
                  The Security Center provides a centralized location for managing security settings and monitoring account activity.
                  Permissioned users can access the Security Center from the Settings view.
                </p>
              </div>

              <div>
                <h4>
                  <span class="toasted white--text px-1 mr-1">Fix</span> Fix Service Worker Registration Error
                </h4>

                <p>
                  A Service Worker registration error has been resolved, ensuring the service worker is properly registered. This restores the application's performance and caching enhancements.
                </p>
              </div>

              <div>
                <h4>
                  <span class="purple white--text px-1 mr-1">Update</span> Release Notes on Docs Page
                </h4>

                <p>
                  The Release Notes have been moved to the Docs page for better visibility and accessibility.
                </p>
              </div>

              <div>
                <h4>
                  <span class="purple white--text px-1 mr-1">Update</span> Navigation Sidebar Enhancements
                </h4>

                <p>
                  The navigation sidebar has been updated with new icons, resized for better visibility. It's now more user-friendly
                  and visually appealing. The sidebar has also been rebranded with the new adops.com logo. The copyright text in the footer has also been updated to reflect this change.
                </p>
              </div>
            </div>

            <!-- March 2025 -->
            <div
              id="2025-03"
            >
              <h3
                :class="`mb-3 subtitle-1 font-weight-bold ${nearblackTextClass}`"
              >
                March 2025
              </h3>

              <div>
                <h4>
                  <span class="primary white--text px-1 mr-1">New</span> Enhanced Time Entry View
                </h4>

                <p>
                  The Time view received a major overhaul with new tab-based navigation,
                  grouping options, and improved date range controls. Internal users
                  can now group entries by client, project, or person.
                </p>
              </div>

              <div>
                <h4>
                  <span class="purple white--text px-1 mr-1">Update</span> Improved Form Validation
                </h4>

                <p>
                  Form validation now provides clearer feedback with inline error messages
                  and visual indicators. Required fields are marked more prominently.
                </p>
              </div>

              <div>
                <h4>
                  <span class="toasted white--text px-1 mr-1">Fix</span> Date Picker Timezone Issues
                </h4>

                <p>
                  Resolved timezone-related issues in date pickers that could cause
                  off-by-one errors near midnight.
                </p>
              </div>
            </div>

            <!-- December 2024 -->
            <div
              id="2024-12"
            >
              <h3
                :class="`mb-3 subtitle-1 font-weight-bold ${nearblackTextClass}`"
              >
                December 2024
              </h3>

              <div>
                <h4>
                  <span class="primary white--text px-1 mr-1">New</span> Organization Dashboard
                </h4>

                <p>
                  A new Organization view provides a card-based overview of all clients
                  with search, filtering by status, and quick navigation to client details.
                </p>
              </div>

              <div>
                <h4>
                  <span class="purple white--text px-1 mr-1">Update</span> Vuetify Component Upgrades
                </h4>

                <p>
                  Data tables, form inputs, and dialog components were upgraded
                  for better performance and accessibility compliance.
                </p>
              </div>

              <div>
                <h4>
                  <span class="toasted white--text px-1 mr-1">Fix</span> Session Persistence
                </h4>

                <p>
                  Fixed an issue where user sessions could expire prematurely
                  during extended usage periods.
                </p>
              </div>
            </div>

            <!-- September 2024 -->
            <div
              id="2024-09"
            >
              <h3
                :class="`mb-3 subtitle-1 font-weight-bold ${nearblackTextClass}`"
              >
                September 2024
              </h3>

              <div>
                <h4>
                  <span class="primary white--text px-1 mr-1">New</span> Refresh Tokens &amp; Silent Auth
                </h4>

                <p>
                  Authentication now uses refresh tokens for seamless session
                  extension without user interruption. Silent re-authentication
                  happens automatically when tokens expire.
                </p>
              </div>

              <div>
                <h4>
                  <span class="purple white--text px-1 mr-1">Update</span> Search Performance
                </h4>

                <p>
                  Global search results now load faster with optimized Firestore
                  queries and client-side caching of recent searches.
                </p>
              </div>
            </div>

            <!-- June 2024 -->
            <div
              id="2024-06"
            >
              <h3
                :class="`mb-3 subtitle-1 font-weight-bold ${nearblackTextClass}`"
              >
                June 2024
              </h3>

              <div>
                <h4>
                  <span class="primary white--text px-1 mr-1">New</span> Export to Excel
                </h4>

                <p>
                  Users can now export table data directly to XLSX format.
                  The export respects current filters, sorting, and column visibility.
                </p>
              </div>

              <div>
                <h4>
                  <span class="purple white--text px-1 mr-1">Update</span> Mobile Responsive Tables
                </h4>

                <p>
                  Data tables now collapse gracefully on smaller screens with
                  horizontal scrolling and priority column display.
                </p>
              </div>

              <div>
                <h4>
                  <span class="toasted white--text px-1 mr-1">Fix</span> Filter State Persistence
                </h4>

                <p>
                  Filters now persist correctly when navigating between views
                  and are properly cleared on explicit reset.
                </p>
              </div>
            </div>

            <!-- March 2024 -->
            <div
              id="2024-03"
            >
              <h3
                :class="`mb-3 subtitle-1 font-weight-bold ${nearblackTextClass}`"
              >
                March 2024
              </h3>

              <div>
                <h4>
                  <span class="primary white--text px-1 mr-1">New</span> Delivery Module Enhancements
                </h4>

                <p>
                  The Pacing and Discrepancy views received new filtering options,
                  improved data visualization, and better handling of large datasets.
                </p>
              </div>

              <div>
                <h4>
                  <span class="purple white--text px-1 mr-1">Update</span> Node.js 20 LTS Migration
                </h4>

                <p>
                  Server infrastructure upgraded to Node.js 20 LTS for improved
                  performance and security. Firebase Functions now use 2nd gen runtime.
                </p>
              </div>
            </div>

            <!-- January 2024 -->
            <div
              id="2024-01"
            >
              <h3
                :class="`mb-3 subtitle-1 font-weight-bold ${nearblackTextClass}`"
              >
                January 2024
              </h3>

              <div>
                <h4>
                  <span class="primary white--text px-1 mr-1">New</span> Tab Query Parameters
                </h4>

                <p>
                  Views with tabs now support URL-based tab selection. Direct links
                  to specific tabs work correctly, and browser back/forward navigation
                  maintains tab state.
                </p>
              </div>

              <div>
                <h4>
                  <span class="purple white--text px-1 mr-1">Update</span> Vue 2.7 Composition API
                </h4>

                <p>
                  Migrated key components to use the Composition API syntax available
                  in Vue 2.7, preparing the codebase for future Vue 3 migration.
                </p>
              </div>

              <div>
                <h4>
                  <span class="toasted white--text px-1 mr-1">Fix</span> Memory Leak in Data Tables
                </h4>

                <p>
                  Resolved a memory leak that occurred when rapidly switching between
                  views with large data tables.
                </p>
              </div>
            </div>

            <div
              id="2023-10"
            >
              <h3
                :class="`mb-3 subtitle-1 font-weight-bold ${nearblackTextClass}`"
              >
                October 2023
              </h3>

              <div>
                <h4>
                  <span class="purple white--text px-1 mr-1">Update</span>Alias Mode (v4) (Generally Available)
                </h4>

                <p>
                  Alias Mode is now open to all internal users. Internal users can enable Alias Mode for
                  their account by navigating to the Settings option that appears on click of their profile
                  in the sidebar. Once in the Settings view, internal users are provided with a dropdown to
                  choose which external user they would like to alias. They can then toggle Alias Mode on,
                  save their settings, and the application will refresh with the aliased user’s settings.
                </p>
              </div>

              <div v-if="isInternalUser">
                <h4>
                  <span class="primary white--text px-1 mr-1">Update</span> iAdOps 5 Beta
                </h4>

                <p>
                  We continue to iterate on iAdOps 5, our next-generation interfaces and APIs. Over the last
                  few months, we've exercised its features and functionality, obtaining valuable feedback
                  from our internal users. Now, we're maturing it from a proof of concept to a
                  production-ready application. More to come as we prepare for another round of
                  general user acceptance testing.
                </p>

                <p>
                  To learn more about iAdOps 5, visit <a
                    href="https://beta.iadops.com/docs"
                    target="_self"
                    class="font-weight-bold"
                  >our Beta documentation</a>.
                </p>
              </div>
            </div>
            <div
              id="2023-08"
            >
              <h3
                :class="`mb-3 subtitle-1 font-weight-bold ${nearblackTextClass}`"
              >
                August 2023
              </h3>

              <div>
                <h4>
                  <span class="purple white--text px-1 mr-1">Update</span> General Maintenance (v4)
                </h4>

                <p>
                  Security and performance audits, alongside refactors and rewrites, were executed
                  to assure the continued stability of our system as we scale.
                </p>
              </div>
            </div>

            <div
              id="2023-05"
            >
              <h3
                :class="`mb-3 subtitle-1 font-weight-bold ${nearblackTextClass}`"
              >
                May 2023
              </h3>

              <div>
                <h4>
                  <span class="coolgray white--text px-1 mr-1">Fix</span> Normalize v4 and v5 Settings (v4)
                </h4>

                <p>
                  To facilitate the transition from v4 to v5, we've updated v4 settings to coexist
                  with v5 settings. This will prevent testers losing settings or encountering
                  unexpected results as they switch between versions.
                </p>
              </div>

              <div>
                <h4>
                  <span class="purple white--text px-1 mr-1">Update</span> Alias Mode Settings (v4) (Limited Audience)
                </h4>

                <p>
                  Alias Mode facilitates internal users’ testing, troubleshooting, and user experience
                  analysis by enabling them to view the application interface and functionality as an
                  external user, also known as the aliased user.
                </p>

                <p>
                  Previously, internal users opened a ticket with the Technology Department requesting
                  Alias Mode be enabled for their account. Now, internal users can enable Alias Mode for
                  their account by navigating to the Settings option that appears on click of their profile
                  in the sidebar. Once in the Settings view, internal users are provided with a dropdown to
                  choose which external user they would like to alias. They can then toggle Alias Mode on,
                  save their settings, and the application will refresh with the aliased user’s settings.
                </p>

                <p>
                  This update also resolves an issue where Alias Mode could deactivate if the internal user
                  updated their user record through actions like marking a message read.
                </p>
              </div>

              <div v-if="isInternalUser">
                <h4>
                  <span class="primary white--text px-1 mr-1">New</span> Viewable Impressions Column for Pacing (Beta)
                </h4>

                <p>
                  The new Viewable Impressions column displays the number of viewable impressions, as reported by
                  Google Ad Manager, for each row in the table.
                </p>

                <p>
                  While this data is part of the calculation made in v4 to determine the Delivered value
                  in the Pacing table, exposing this column apart from Delivered allows users to analyze
                  the data in a new way.
                </p>

                <p>
                  Currently in <a
                    href="https://beta.iadops.com"
                    target="_blank"
                  ><strong>Beta</strong></a>.
                </p>
              </div>

              <div v-if="isInternalUser">
                <h4>
                  <span class="primary white--text px-1 mr-1">New</span> iAdOps 5 Beta
                </h4>

                <p>
                  iAdOps 5 marks a major release for our application. Over the last year,
                  we've rebuilt core components from the ground up, using the latest technologies
                  and best practices to deliver a faster, more reliable, and user-friendly experience
                  for our users.
                </p>

                <p>
                  We've also introduced user customizations to power our new Bookmark and Network Settings.
                  Check out Dark Mode, Full-Screen Mode, and more as you navigate throughout the application.
                </p>

                <p>
                  To learn more about iAdOps 5, visit <a
                    href="https://beta.iadops.com/docs"
                    target="_self"
                    class="font-weight-bold"
                  >our Beta documentation</a>.
                </p>
              </div>
            </div>

            <div
              id="2022-06"
            >
              <h3
                :class="`mb-3 subtitle-1 font-weight-bold ${nearblackTextClass}`"
              >
                June 2022
              </h3>

              <div>
                <h4>
                  <span class="purple white--text px-1 mr-1">Update</span> Access Table Menus via Double-Click
                </h4>

                <p>
                  Users who prefer double-clicking over right-clicking can now access table menus
                  via their preferred method. Right-clicking and double-clicking produce the same outcome,
                  a context menu for the row or table header selected.
                </p>
              </div>

              <div>
                <h4>
                  <span class="coolgray white--text px-1 mr-1">Fix</span> 10-Day History Requests
                </h4>

                <p>
                  The 10-Day History view, available on right-click of Pacing rows, should render
                  the expected table after sending a well-formed request to the iAdOps API
                  and receiving the expected response.
                </p>
              </div>

              <div>
                <h4>
                  <span class="primary white--text px-1 mr-1">New</span> Server
                </h4>

                <p>
                  We've integrated our application with a thin server that supports secure,
                  cross-server communications between privileged users, recognized systems, and itself.
                </p>
              </div>
            </div>

            <div
              id="2022-05"
            >
              <h3
                :class="`mb-3 subtitle-1 font-weight-bold ${nearblackTextClass}`"
              >
                May 2022
              </h3>
              <div>
                <h4>
                  <span class="primary white--text px-1 mr-1">New</span> Group-Level Metrics
                </h4>

                <p>
                  Calculates the metrics for each group of rows, then renders them to the
                  appropriate group header.
                </p>
              </div>

              <div>
                <h4>
                  <span class="purple white--text px-1 mr-1">Update</span> AdX/OB Report Iterations
                </h4>

                <p>
                  User feedback continues leading the development of our programmatic reporting.
                  Updates to its merge, group, and filter options have been implemented to ease
                  new users into the data and interface. Most notably, default settings group
                  the data by Demand Source, and group-level metrics have been added to group
                  headers. We also introduced week-level logic to empower week-over-week analysis.
                </p>

                <p>
                  Available to a limited audience.
                  <a
                    href="mailto:tech@oao.freshdesk.com"
                    target="_blank"
                  >
                    Open a ticket
                  </a>
                  to request access.
                </p>
              </div>

              <div>
                <h4>
                  <span class="purple white--text px-1 mr-1">Update</span> Embed Custom Reports via Tableau Connected Apps
                </h4>

                <p>
                  Tableau Connected Apps are the latest embed option allowing iAdOps to seamlessly integrate
                  Custom Reports with our web components. Users no longer need to sign in to Tableau after
                  verifying their account with iAdOps, as iAdOps will handle the auth workflow behind the scenes.
                </p>
              </div>
            </div>

            <div
              id="2022-04"
            >
              <h3
                :class="`mb-3 subtitle-1 font-weight-bold ${nearblackTextClass}`"
              >
                April 2022
              </h3>

              <div>
                <h4>
                  <span class="purple white--text px-1 mr-1">Update</span> AdX/OB Report Iterations
                </h4>

                <p>
                  Programmatic reports for AdX/OB, AdX Health, and Network Health have been released
                  to a limited subset of external users. Access logic has been updated accordingly.
                </p>

                <p>
                  Available to a limited audience.
                  <a
                    href="mailto:tech@oao.freshdesk.com"
                    target="_blank"
                  >
                    Open a ticket
                  </a>
                  to request access to iAdOps' programmatic reports.
                </p>
              </div>

              <div>
                <h4>
                  <span class="coolgray white--text px-1 mr-1">Fix</span> Preview &amp; Tearsheet
                </h4>

                <p>
                  The Preview & Tearsheet view, available on right-click of Pacing rows, should render
                  the expected list of creatives after sending a well-formed request to the iAdOps API
                  and receiving the expected response. The previous app version was sending a request
                  that did not meet the API specifications, leading to a 404 response from the iAdOps API.
                </p>

                <p>
                  If you encounter a Preview &amp; Tearsheet issue after April 13, you may wish to clear your cache or
                  <a
                    href="mailto:tech@oao.freshdesk.com"
                    target="_blank"
                  >
                    open a ticket
                  </a> to report the possible regression.
                </p>
              </div>

              <div>
                <h4>
                  <span class="purple white--text px-1 mr-1">Update</span> Cache Controls
                </h4>

                <p>
                  Users' browsers have been loading files across versions, leading to unexpected,
                  inconsistent application behavior because files from different version are interacting.
                  This update prompts users' browsers to reference the network before relying on the cache.
                </p>

                <p>
                  Users may experience a performance hit on their initial load of this version's
                  various files while browsers update their cache. The New Content prompt will
                  indicate this to the user so they're aware of the change impacting their cache.
                </p>
              </div>
            </div>

            <div
              id="2022-03"
            >
              <h3
                :class="`mb-3 subtitle-1 font-weight-bold ${nearblackTextClass}`"
              >
                March 2022
              </h3>

              <div>
                <h4>
                  <span class="purple white--text px-1 mr-1">Update</span> Redirect v1 Reports Route to Dashboard
                </h4>

                <p>
                  Some users ported from v1 may be accustomed to the v1 Reports route,
                  so we've configured a redirect from /reports to the Dashboard on the home page.
                </p>
              </div>

              <div>
                <h4>
                  <span class="purple white--text px-1 mr-1">Update</span> Expose IDs via Column Controls
                </h4>

                <p>
                  Users can expose ID/Code columns via the Column Controls view, available to users
                  on right-click of any column header.  This adjusts for recent updates to our
                  Column Controls and Export logic, which align the export and view more closely,
                  no longer assuming ID/Code columns are desirable in the export if they're hidden.
                  in the web interface.
                </p>
              </div>

              <div>
                <h4>
                  <span class="purple white--text px-1 mr-1">Update</span> User Analytics
                </h4>

                <p>
                  Modernizes and extends our user analytics setup to address recent
                  dependency updates, like the move to modular loading and changes to event logging.
                </p>
              </div>

              <div>
                <h4>
                  <span class="coolgray white--text px-1 mr-1">Fix</span> Render of 10-Day History's CTR & Viewable Impressions
                </h4>

                <p>
                  The 10-Day History reports' CTR and Viewable Impressions columns should appear as
                  percentages and numbers, respectively.
                </p>
              </div>

              <div>
                <h4>
                  <span class="coolgray white--text px-1 mr-1">Fix</span> Exports' Sorting & Formatting
                </h4>

                <p>
                  Exports should reflect the view's sorting, while numerical and percentage values
                  should export accurately and consistently.
                  If you encounter an export issue after March 1st, you may wish to clear your cache or
                  <a
                    href="mailto:tech@oao.freshdesk.com"
                    target="_blank"
                  >
                    open a ticket
                  </a> to report the possible regression.
                </p>
              </div>
            </div>

            <div
              id="2022-02"
            >
              <h3
                :class="`mb-3 subtitle-1 font-weight-bold ${nearblackTextClass}`"
              >
                February 2022
              </h3>

              <div>
                <h4>
                  <span class="coolgray white--text px-1 mr-1">Fix</span> Documentation Permissions
                </h4>

                <p>
                  Permissions have been updated to assure all users can access the PDF
                  documentation linked in the User Docs page, and all users can snooze
                  or permanently dismiss feature prompts. If you encounter a related
                  issue after February 28th, you may wish to clear your cache or
                  <a
                    href="mailto:tech@oao.freshdesk.com"
                    target="_blank"
                  >
                    open a ticket
                  </a> to report the possible regression.
                </p>
              </div>

              <div>
                <h4>
                  <span class="primary white--text px-1 mr-1">New</span> Select All &amp; Toggle All Options
                </h4>

                <p>
                  With the new "Select All" and "Toggle All" options, users can quickly include or
                  exclude all options listed for an input, empowering them to enter a state where they
                  can deselect undesired options. This is particularly useful when users are faced with
                  numerous options and the need to select all or most of those options.
                </p>
              </div>

              <div>
                <h4>
                  <span class="purple white--text px-1 mr-1">Update</span> Date Sorting
                </h4>

                <p>
                  Date sorting silently failed to convert dates to a Unix timestamp our sorting logic could process.
                  This is now fixed in the current version.
                </p>

                <p>
                  If you encounter this issue after February 28, 2022, you may wish to clear your cache or
                  <a
                    href="mailto:tech@oao.freshdesk.com"
                    target="_blank"
                  >
                    open a ticket
                  </a>.
                </p>
              </div>

              <div>
                <h4>
                  <span class="purple white--text px-1 mr-1">Update</span> Extended Event Monitoring
                </h4>

                <p>
                  With greater observability into our application, our engineers have more information
                  supporting their development efforts, leading to fewer bugs reaching production
                  and faster issue resolution when they do.
                </p>
              </div>
            </div>

            <div
              id="2022-01"
            >
              <h3
                :class="`mb-3 subtitle-1 font-weight-bold ${nearblackTextClass}`"
              >
                January to February 2022
              </h3>

              <div>
                <h4>
                  <span class="purple white--text px-1 mr-1">Update</span> New Data Source for Discrepancy Reports
                </h4>

                <p>
                  The Discrepancy report has been updated on the backend with new data sources.
                  Some numbers may be slightly different due to the resulting changes to our data strategy.
                  <a
                    href="mailto:tech@oao.freshdesk.com"
                    target="_blank"
                  >
                    Open a ticket
                  </a> should you encounter any missing or markedly inconsistent data.
                </p>
              </div>

              <div>
                <h4>
                  <span class="purple white--text px-1 mr-1">Update</span> General Maintenance
                </h4>

                <p>
                  Security and performance audits, alongside refactors and rewrites, were executed
                  to assure the continued stability of our system as we scale.
                </p>
              </div>
            </div>

            <div
              id="2021-12"
            >
              <h3
                :class="`mb-3 subtitle-1 font-weight-bold ${nearblackTextClass}`"
              >
                December 2021
              </h3>

              <div>
                <h4>
                  <span class="purple white--text px-1 mr-1">Update</span> Table Pagination: New Page Count
                </h4>

                <p>
                  The table pagination tool is located in the page header of every table view.
                  Updates include the current page number, the total page count, and a more
                  minimal design of the prev/next controls, represented by arrow icons.
                </p>
              </div>

              <div>
                <h4>
                  <span class="purple white--text px-1 mr-1">Update</span> Rows Per Page: New Row Range and Row Count
                </h4>

                <p>
                  Alongside the table pagination tool, the Rows Per Page tool, also located in the
                  header of every table view, has been updated.
                </p>
                <p>
                  The Rows Per Page tool's label now includes the index of the rows on the
                  page, as well as the total row count, e.g. "Rows 1 - 100 of 1,000".
                </p>

                <p>
                  We've also updated the options' text for clarity, adding "/ Page" to
                  relevant options, e.g. "100 / Page".
                </p>
              </div>

              <div>
                <h4>
                  <span class="primary white--text px-1 mr-1">New</span> Context Menus Replace Table Icons
                </h4>

                <p>
                  Right-click any column header or row to access the new context menu.
                  The right-click action replaces the click of iconography once bound to the left
                  of every column header and row. The new interaction via context menus allows users
                  to access actions wherever they may be in the table. At once, it boosts performance
                  at the time of table renders by simplifying the HTML inside every table cell.
                  That means users can engage with their data faster than previous versions allowed.
                </p>
              </div>

              <div>
                <h4>
                  <span class="purple white--text px-1 mr-1">Update</span> Column Resizer: Faster and Easier to Use
                </h4>

                <p>
                  Users can drag the new column header icon to resize columns quickly and easily.
                </p>

                <p>
                  The first iteration of the column resizer suffered experience issues due to numerous
                  style recalculations impacting performance, among other things. While we revisit
                  that implementation, we've surfaced an icon in each column header that performs
                  that same functionality without the lag.
                </p>
              </div>

              <div>
                <h4>
                  <span class="primary white--text px-1 mr-1">New</span> Organization, Person, & Project Pages Get Saved Filters
                </h4>

                <p>
                  All internal users can now leverage the Saved Filters at the top of our Organization,
                  Person, and Project pages.
                </p>

                <p>
                  Don't see your favorite filters among the current options?
                  <a
                    href="mailto:tech@oao.freshdesk.com"
                    target="_blank"
                  >
                    Open a ticket
                  </a>
                  to request its addition.
                </p>
              </div>

              <div>
                <h4>
                  <span class="midgray white--text px-1 mr-1">Fix</span> Custom Reports Navigation
                </h4>

                <p>
                  Stabilized render of Custom Reports for Users navigating between the Custom Reports
                  Dashboard and a Custom Report.
                </p>

                <p>
                  Users navigating between the Custom Reports Dashboard and a Custom Report may have
                  seen a blank page, as opposed to users who deep linked into the Custom Report,
                  who always saw the Sign In page or report, as intended.
                </p>
              </div>
            </div>

            <div
              id="2021-11"
            >
              <h3
                class="mb-3 subtitle-1 primary--text font-weight-bold"
              >
                November 2021
              </h3>

              <div>
                <h4>
                  Resize Columns
                </h4>

                <p>
                  Hover over the right edge of every table cell to expose the Column Resizer.
                  Click and drag the element horizontally to set the selected column's width.
                  Click again to release the column.
                </p>
              </div>

              <div>
                <h4>
                  Order Columns
                </h4>

                <p>
                  Re-arrange columns for faster analysis using the Column Picker
                  located in every table's header. Exports uphold column order.
                </p>
              </div>

              <div>
                <h4>
                  Freeze Columns
                </h4>

                <p>
                  Pin columns to freeze them in place using the Column Picker
                  located in every table's header.
                </p>
              </div>

              <div>
                <h4>Numerous Stability Enhancements</h4>

                <p>
                  Among recent updates, we've iterated on the accessibility
                  of our search input to improve the experience for users
                  navigating via keyboard, voice over, and voice control.
                </p>
              </div>
            </div>

            <div
              id="2021-10"
            >
              <h3
                class="mb-3 subtitle-1 primary--text font-weight-bold"
              >
                October 2021
              </h3>

              <div>
                <h4>
                  Group & Merge By More Dimensions
                </h4>

                <p>
                  The Group and Merge dropdowns include numerous more dimensions
                  to breakdown or summarize as needed. Open the Tools menu in
                  every report's header to access this feature.
                </p>

                <p>
                  Don't see your favorite dimension among the latest options?
                  <a
                    href="mailto:tech@oao.freshdesk.com"
                    target="_blank"
                  >
                    Open a ticket
                  </a>
                  to request its addition.
                </p>
              </div>

              <div>
                <h4>
                  Enhanced Organization- and User-Level Permissions & Settings
                </h4>

                <p>
                  Not only has security improved in this release,
                  the options for back-end customizations have too.
                  <a
                    href="mailto:tech@oao.freshdesk.com"
                    target="_blank"
                  >
                    Open a ticket
                  </a>
                  to request customizations to your table views &mdash;
                  from sorting and column visibility to grouping,
                  merging, pre-filtering, and more.
                </p>
              </div>

              <div>
                <h4>
                  AdX and OB Updates
                </h4>

                <p>
                  We continue to iterate on the AdX and OB reports with our
                  Early Adopters' feedback in mind.
                  Hover over column headers to learn more where needed, or use
                  new Merge options to summarize across a greater range of fields.
                </p>

                <p>
                  Available to a limited internal audience.
                  <a
                    href="mailto:tech@oao.freshdesk.com"
                    target="_blank"
                  >
                    Open a ticket
                  </a>
                  to request access.
                </p>
              </div>
            </div>

            <div
              id="2021-09"
            >
              <h3
                class="mb-3 subtitle-1 primary--text font-weight-bold"
              >
                September 2021
              </h3>

              <div>
                <h4>
                  Custom Reports Admin Dashboard
                </h4>

                <p>
                  Admins can review which reports we've embedded alongside
                  which users have been granted access to each report.
                </p>
              </div>

              <div>
                <h4>Numerous Stability Enhancements</h4>

                <p>
                  Among recent updates, we've iterated on the v3 release with
                  maintenance and scalability in mind. Security and dependency
                  management were also addressed.
                </p>
              </div>
            </div>

            <div
              id="2021-08"
            >
              <h3
                class="mb-3 subtitle-1 primary--text font-weight-bold"
              >
                August 2021
              </h3>

              <div>
                <h4>
                  Distinguishes Between Creatives With & Without Preview URLs
                </h4>

                <p>
                  In the Tearsheet feature, we've signaled to users when a creative
                  cannot produce a preview URL.
                </p>
              </div>

              <div>
                <h4>
                  Sets Default Date Range on Overview: Last Month to Date
                </h4>

                <p>
                  To manage the Overview page's performance issues, impacted
                  over time by the growing amount of data loaded to the table,
                  we reduced the set of initially rendered items to those from
                  the beginning of last month to now. Users can use the filter
                  to query the requested data beyond this initial date range.
                </p>
              </div>
            </div>

            <div
              id="2021-07"
            >
              <h3
                class="mb-3 subtitle-1 primary--text font-weight-bold"
              >
                July 2021
              </h3>

              <div>
                <h4>
                  Saved Filters (Limited)
                </h4>

                <p>
                  Saved Filter views are available on request and visible
                  to users with access. This is the first step toward
                  releasing the feature to a wider audience.
                </p>
              </div>

              <div>
                <h4>
                  User Aliasing
                </h4>

                <p>
                  Internals can now adopt the identity of an iAdOps external user,
                  giving them the ability to view iAdOps just as their user does.
                </p>
              </div>

              <div>
                <h4>
                  Multi-Sort
                </h4>

                <p>
                  To access this feature, click on the table header you wish to sort by.
                  Continue clicking to toggle through the options. Options are ASC, DSC, and off.
                </p>
              </div>

              <div>
                <h4>
                  Continued Performance Improvements
                </h4>

                <p>
                  Manipulate larger data sets. Our latest performance enhancements
                  have optimized our interactions for network speeds and device
                  memory.
                </p>
              </div>

              <div v-if="isUserInternalDev">
                <h4 class="toasted--text text-uppercase">
                  Known Issues
                </h4>

                <ul class="mb-6">
                  <li>
                    Column Widths, Headers, & Tooltips
                  </li>
                  <li>
                    Refine Time Form Rules on Duration
                  </li>
                  <li>
                    Export Sorting Reflects Web View's Sorting
                  </li>
                  <li>
                    Flash of "Choose a Publisher" View at Page Load
                  </li>
                  <li>
                    Demo Mode Updates
                  </li>
                  <li>
                    Toggle Groups' Performance is Slow if Numerous Groups
                  </li>
                  <li>
                    Performance of Date Filter at Network Health
                  </li>
                  <li>
                    Performance of Export as XLSX
                  </li>
                  <li>
                    Performance of Table Render
                  </li>
                  <li>
                    Cross-Link Between Prod and Beta
                  </li>
                </ul>
              </div>
            </div>

            <div
              id="2021-06"
            >
              <h3
                class="mb-3 subtitle-1 primary--text font-weight-bold"
              >
                June 2021
              </h3>

              <div>
                <h4>
                  New Dashboard Card: Favorite Reports
                </h4>

                <p>
                  The Dashboard received an update to accommodate its growth,
                  which begins with the new Reports card. For externals,
                  this lists all their available reports by network. For internals,
                  this lists the reports they've indicated are their favorites.
                </p>
              </div>

              <div>
                <h4>
                  Performance
                </h4>

                <p>
                  Manipulate larger data sets. Our latest performance enhancements
                  have optimized our interactions for network speeds and device
                  memory.
                </p>
              </div>

              <div>
                <h4>Informative Progress Loaders and Error Messages</h4>

                <p>
                  Developers and users alike will receive more informative
                  messages from the application at key interaction points,
                  including error states.
                </p>
              </div>
            </div>

            <div
              id="2021-05"
            >
              <h3
                class="mb-3 subtitle-1 primary--text font-weight-bold"
              >
                May 2021
              </h3>

              <div>
                <h4>
                  AdX Health Reports
                </h4>

                <p>
                  Monitor AdX performance and coverage.
                </p>

                <p>
                  The AdX Health Report is part of our new Programmatic collection.
                  Networks configured for our programmatic views will appear among the omnisearch results.
                </p>

                <v-tooltip left>
                  <template v-slot:activator="{ on: tooltipOn, attrs: tooltipAttrs }">
                    <div
                      v-if="isInternalUser"
                      class="cursor-pointer"
                    >
                      <p
                        v-bind="tooltipAttrs"
                        v-on="tooltipOn"
                      >
                        You can also view which organizations are configured for this report by visiting
                        the Organizations page.
                        <span
                          class="toasted--text font-weight-bold"
                        >
                          *
                        </span>
                      </p>
                    </div>
                  </template>

                  <span>Internal Note</span>
                </v-tooltip>

                <p>
                  Available to a limited internal audience.
                  <a
                    href="mailto:tech@oao.freshdesk.com"
                    target="_blank"
                  >
                    Open a ticket
                  </a>
                  to request access.
                </p>
              </div>

              <div>
                <h4>
                  Network Health Reports
                </h4>

                <p>
                  Monitor the network to spot unusual behavior across any device or platform.
                </p>

                <p>
                  The Network Health Report is part of our new Programmatic collection.
                  Networks configured for our programmatic views will appear among the omnisearch results.
                </p>

                <v-tooltip left>
                  <template v-slot:activator="{ on: tooltipOn, attrs: tooltipAttrs }">
                    <div
                      v-if="isInternalUser"
                      class="cursor-pointer"
                    >
                      <p
                        v-bind="tooltipAttrs"
                        v-on="tooltipOn"
                      >
                        You can also view which organizations are configured for this report by visiting
                        the Organizations page.
                        <span
                          class="toasted--text font-weight-bold"
                        >
                          *
                        </span>
                      </p>
                    </div>
                  </template>

                  <span>Internal Note</span>
                </v-tooltip>

                <p>
                  Available to a limited internal audience.
                  <a
                    href="mailto:tech@oao.freshdesk.com"
                    target="_blank"
                  >
                    Open a ticket
                  </a>
                  to request access.
                </p>
              </div>

              <div>
                <h4>Numerous Stability Enhancements</h4>

                <p>
                  Among recent updates, we've extended our filters' automated tests,
                  iterated on our sign-in error messages to provide users with more context,
                  and updated our error handling to notify users when their
                  network is unavailable.
                </p>
              </div>
            </div>

            <div
              id="2021-04"
            >
              <h3
                class="mb-3 subtitle-1 primary--text font-weight-bold"
              >
                April 2021
              </h3>

              <div>
                <h4>
                  Export as XLSX
                </h4>

                <p>
                  To access this feature, click on a report's Export icon, located in every report's header.
                </p>
              </div>

              <div>
                <h4>
                  AdX & OB Reports
                </h4>

                <p>
                  Monitor AdX & OB delivery to ensure all partners are delivering.
                </p>

                <p>
                  The AdX & OB Reports, fka the Yield Partner Reports, are part of our new Programmatic collection.
                  Networks configured for our programmatic views will appear among the omnisearch results.
                </p>

                <v-tooltip left>
                  <template v-slot:activator="{ on: tooltipOn, attrs: tooltipAttrs }">
                    <div
                      v-if="isInternalUser"
                      class="cursor-pointer"
                    >
                      <p
                        v-bind="tooltipAttrs"
                        v-on="tooltipOn"
                      >
                        You can also view which organizations are configured for this report by visiting
                        the Organizations page.
                        <span
                          class="toasted--text font-weight-bold"
                        >
                          *
                        </span>
                      </p>
                    </div>
                  </template>

                  <span>Internal Note</span>
                </v-tooltip>

                <p>
                  Available to a limited internal audience.
                  <a
                    href="mailto:tech@oao.freshdesk.com"
                    target="_blank"
                  >
                    Open a ticket
                  </a>
                  to request access.
                </p>
              </div>

              <div v-if="isInternalUser">
                <h4>
                  <v-tooltip left>
                    <template v-slot:activator="{ on: tooltipOn, attrs: tooltipAttrs }">
                      <div class="cursor-pointer">
                        <span
                          v-bind="tooltipAttrs"
                          v-on="tooltipOn"
                        >
                          New Columns and Filters in Organization
                        </span>
                        <span
                          class="toasted--text"
                        >
                          *
                        </span>
                      </div>
                    </template>

                    <span>Internal Note</span>
                  </v-tooltip>
                </h4>

                <p>
                  With the addition of new programmatic reports, we've exposed columns to
                  the Organization views indicating whether an organization is configured
                  for programmatic reports.
                </p>
              </div>

              <div>
                <h4>
                  Merge Rows to Summarize
                </h4>

                <p>
                  Users can merge rows to create summaries, e.g. Advertiser Summaries.
                  Combine with grouping to gain insights into performance across categories.
                </p>
              </div>

              <div v-if="isInternalUser">
                <h4>Published Backlog</h4>

                <p>
                  Grants our users visibility over their feedback's movement through our process.
                  This first iteration captures much of the user feedback provided during Q1's user scoring
                  exercises.
                </p>
              </div>

              <div>
                <h4>Numerous Stability Enhancements</h4>

                <p>
                  Highlights include various refactors to optimize performance at load time,
                  plus vast improvements to our automated tests' reporting, particularly around coverage.
                </p>
              </div>
            </div>

            <div
              id="2021-03"
            >
              <h3
                class="mb-3 subtitle-1 primary--text font-weight-bold"
              >
                March 2021
              </h3>

              <div v-if="isInternalUser">
                <h4>
                  <v-tooltip left>
                    <template v-slot:activator="{ on: tooltipOn, attrs: tooltipAttrs }">
                      <div class="cursor-pointer">
                        <span
                          v-bind="tooltipAttrs"
                          v-on="tooltipOn"
                        >
                          Extended User Analytics
                        </span>
                        <span
                          class="toasted--text"
                        >
                          *
                        </span>
                      </div>
                    </template>

                    <span>Internal Note</span>
                  </v-tooltip>
                </h4>

                <p>
                  Our Google Analytics code has been updated for accuracy
                  and consistency, making our reports more reliable and
                  insightful. Event captures are next.
                </p>
              </div>

              <div v-if="isInternalUser">
                <h4>
                  <v-tooltip left>
                    <template v-slot:activator="{ on: tooltipOn, attrs: tooltipAttrs }">
                      <div class="cursor-pointer">
                        <span
                          v-bind="tooltipAttrs"
                          v-on="tooltipOn"
                        >
                          Organization and Person Profiles: New Look & Linked Network Codes
                        </span>
                        <span
                          class="toasted--text"
                        >
                          *
                        </span>
                      </div>
                    </template>

                    <span>Internal Note</span>
                  </v-tooltip>
                </h4>

                <p>
                  Organization and Person pages have been restructured to
                  accommodate the growing amount of information we're
                  capturing through our Internal Data efforts. As a bonus,
                  network codes now link to GAM.
                </p>
              </div>

              <div>
                <h4>
                  Adds Advertiser Column and Filter to Discrepancy
                </h4>

                <p>
                  Group, filter, and sort your Discrepancy reports by
                  Advertiser Name using this new column and filter.
                  <a
                    href="mailto:tech@oao.freshdesk.com"
                    target="_blank"
                  >
                    Open a ticket
                  </a>
                  to have specific advertisers prefiltered from your reports.
                </p>
              </div>

              <div>
                <h4>Additional Columns & Corresponding Filters Now on Pacing</h4>

                <p>
                  Analyze your revenue at risk using the new revenue-related
                  columns, listed below. Users can combine revenue filters
                  with the new Custom Fields to create custom reports.
                </p>

                <p>
                  <ul>
                    <li>All Revenue</li>
                    <li>Potential Lost Revenue</li>
                    <li>Potential Lost Revenue Percentage</li>
                    <li>Custom Fields</li>
                  </ul>
                </p>

                <p>
                  Details, including calculations, have been defined in the
                  Pacing legend, available in Pacing reports' headers.
                </p>
              </div>

              <div>
                <h4>
                  Adds CTR Column to Line Item-Level Reports
                </h4>

                <p>
                  Historical and Creatives report also received new columns.
                  Users will find Click-Thru Rate (CTR) has been exposed
                  beside the existing Clicks column.
                </p>
              </div>

              <div v-if="isInternalUser">
                <h4>
                  <v-tooltip left>
                    <template v-slot:activator="{ on: tooltipOn, attrs: tooltipAttrs }">
                      <div class="cursor-pointer">
                        <span
                          v-bind="tooltipAttrs"
                          v-on="tooltipOn"
                        >
                          Demo Mode
                        </span>
                        <span
                          class="toasted--text"
                        >
                          *
                        </span>
                      </div>
                    </template>

                    <span>Internal Note</span>
                  </v-tooltip>
                </h4>

                <p>
                  Share your iAdOps view without sharing sensitive data.
                  Click on the user profile in the sidebar to access
                  Demo Mode via the Settings menu.
                </p>
              </div>

              <div>
                <h4>Limited Programmatic Views</h4>

                <p>
                  Ad Exchange data is available to a limited audience.
                  More to come as we continue developing these views
                  with early adopters' guidance.
                </p>

                <p>
                  <a
                    href="mailto:tech@oao.freshdesk.com"
                    target="_blank"
                  >
                    Open a ticket
                  </a>
                  to request early access to these views.
                </p>
              </div>

              <div>
                <h4>Numerous Stability Enhancements</h4>

                <p>
                  Minor enhancements were made to improve the general
                  user experience. For example, layouts were altered
                  to reduce breaks across browsers and devices.
                </p>
              </div>
            </div>

            <div
              id="2021-02"
            >
              <h3
                class="mb-3 subtitle-1 primary--text font-weight-bold"
              >
                February 2021
              </h3>

              <p>
                Originally released to
                <a
                  href="https://beta.iadops.com"
                  target="_blank"
                >
                  Beta
                </a>
                in February,
                these updates are on iadops.com as of March 2021.
              </p>

              <div>
                <h4>
                  Data Freshness Indicator Represents When Data Is Refreshing
                </h4>

                <p>
                  The latest updates to the Data Status Indicator inform
                  users when the data refresh cycle is ongoing. During this time,
                  databases are updating and page loads may be slow.
                </p>
              </div>

              <div>
                <h4>
                  Sales Person, Trafficker, and Secondary Trafficker Columns
                </h4>

                <p>
                  Three additional columns are available in Pacing today:
                  Sales Person, Trafficker, and Secondary Traffickers.
                  Use the column picker in the header to manage their visibility
                  in the browser and export.
                </p>
              </div>

              <div>
                <h4>Numerous Stability Enhancements</h4>

                <p>
                  We're working behind the scenes to assure the app remains
                  stable, responsive, and secure.
                </p>
              </div>
            </div>

            <div
              id="2021-01"
            >
              <h3
                class="mb-3 subtitle-1 primary--text font-weight-bold"
              >
                January 2021
              </h3>

              <div>
                <h4>Dynamic Grouping</h4>

                <p>
                  Now available to all users, dynamic grouping empowers
                  users to group table rows by an array of properties
                  based on the report. Pacing and Discrepancy reports
                  can be grouped by Advertiser, Order, or
                  Advertiser &amp; Order.
                </p>

                <p>
                  This option is located in the Filter menu in the top right
                  of the application's header.
                </p>

                <p>
                  Later iterations will empower users to save group preferences,
                  group by a larger set of properties, and gain group-level
                  insights via calculated metrics.
                </p>
              </div>

              <div>
                <h4>
                  Layout Options
                </h4>

                <p>
                  A new Layout option has been added to the Action icons
                  in the header. Clicking on the icon to the left of the
                  Filters menu reveals a dropdown. Options include "Toggle
                  Groups" (see Dynamic Grouping) and "Toggle Saved Filters."
                </p>

                <p>
                  When a Group property is set, users can use the "Toggle
                  Groups" option to toggle table rows in and out of view.
                  Performance of this feature will scale relative to the
                  number of tables on the page.
                </p>

                <p>
                  "Toggle Groups" is disabled if no Group property has
                  been set. Help text has been provided in context to guide
                  new users.
                </p>

                <p>
                  "Toggle Saved Filters" hides the Saved Filters above the
                  line item content, lending users greater screen real
                  estate for tabular analysis when necessary.
                </p>
              </div>

              <div>
                <h4>
                  OSI Column Relocated
                </h4>

                <p>
                  The OSI column has been moved to the right of Priority
                  for easier viewing when all columns are visible.
                </p>
              </div>

              <div>
                <h4>
                  Legend Icon
                </h4>

                <p>
                  We added this new action icon in the header area to
                  make it easy to view the OSI, color definitions, and
                  discrepancy calculations. The legend will provide
                  in-context info depending on whether you're in the
                  Pacing or Discrepancy report views.
                </p>
              </div>

              <div>
                <h4>
                  Action Icon Tooltips
                </h4>

                <p>
                  These floating tooltips will appear on hover to help
                  easily identify each icon.
                </p>
              </div>
            </div>

            <div
              id="2020-12"
            >
              <h3
                class="mb-3 subtitle-1 primary--text font-weight-bold"
              >
                December 2020
              </h3>

              <div>
                <h4>Dynamic Grouping (BETA)</h4>

                <p>
                  Group table rows by an array of properties based on
                  the report. Pacing and Discrepancy reports can be
                  grouped by advertisers, orders, or a combination of
                  the two.
                </p>
              </div>

              <div v-if="isInternalUser">
                <h4>Internal Enhancements</h4>

                <p>
                  Addresses row height issues impacting Org table<br>
                  Assert User Record Management Rules via Forms<br>
                  Iterates on User Settings and Related Forms<br>
                  Filter and sort Person’s generated properties (level, role, manager, org)<br>
                  Org Pages Expose New Properties and Filters, Including Network Code, Pacing, Discrepancy, and Timesheet Information<br>
                  Org Profile Pages Sport a New Look to Accommodate Team-Level Data<br>
                  Person profile pages include Access and Organization properties<br>
                  Exposes Change Log details to Person and Org profile pages<br>
                </p>
              </div>

              <div>
                <h4>Other Enhancements</h4>

                <p>
                  Moves Actions to Left of Line Items<br>
                  Updated styles for consistent branding and layouts<br>
                </p>
              </div>
            </div>

            <div
              id="2020-11"
            >
              <h3
                class="mb-3 subtitle-1 primary--text font-weight-bold"
              >
                November 2020
              </h3>
              <div>
                <h4>Clicks &amp; CTR</h4>

                <p>
                  Users expressed Clicks and Click-Through Rate are
                  important metrics for them. As such, we’ve added
                  them as columns to all Pacing reports. View, sort,
                  and filter the Pacing report by Clicks and CTR.
                </p>
              </div>

              <div>
                <h4>Column-Picking</h4>

                <p>
                  To hone in on specific values or otherwise limit the data
                  on the page, use the new Column-Picker to the left of a
                  table's column headers.
                </p>

                <p>
                  Users are able to choose the columns visible in their
                  Pacing and Discrepancy Reports. While the first
                  iteration will focus on the single-session experience,
                  later iterations will save column preferences across
                  users’ sessions and devices.
                </p>
              </div>

              <div>
                <h4>Export Creatives Report</h4>

                <p>
                  Take Creatives data with you. Share it, transform it,
                  or simply save it for later reference. Users will find
                  a new export icon appearing in the top left of all
                  Historical and Creative Reports.
                </p>
              </div>

              <div>
                <h4>Export Historical Report</h4>

                <p>
                  Take Historical Pacing data with you. Share it, transform
                  it, or simply save it for later reference. Users will
                  find a new export icon appearing in the top left of all
                  Historical and Creative Reports.
                </p>
              </div>

              <div>
                <h4>OmniSearch Enhancements: See All Search Results</h4>

                <p>
                  Users can toggle between viewing all search results or
                  limiting them to 3 per section. View all results for a
                  search by pressing the results total, then click again
                  to limit them. Result totals appear to the left of each
                  results’ section headers.
                </p>
              </div>

              <div>
                <h4>
                  OmniSearch Enhancements: Type-to-Search From Any Character Index
                </h4>

                <p>
                  Users can search for teams quickly with these updates to
                  the omnisearch's configuration. For example, typing "AMP"
                  would produce "Example." Searches will still prioritize
                  matches at the start of the text.
                </p>
              </div>

              <div>
                <h4>Smarter User Analytics</h4>

                <p>
                  Adds Event Tracking<br>
                </p>
              </div>
            </div>

            <div
              id="2020-10"
            >
              <h3
                class="mb-3 subtitle-1 primary--text font-weight-bold"
              >
                September - October 2020
              </h3>

              <div>
                <h4>Creative Delivery Report</h4>

                <p>
                  In addition to displaying the creative ad size
                  delivered per each line item in the UI, we have
                  also released an option for downloading the 10-day
                  Creative Report for each line item. This report is
                  available in the hamburger menu associated with
                  each line item. It is one of the options alongside
                  Tearsheets and the Historical report.
                </p>
              </div>

              <div>
                <h4>Creative Ad Sizes</h4>

                <p>
                  We have included this column based on 'Ad Size Delivered'
                  in Google's metric. This metric is not quite the same as
                  which ad sizes have delivered for a specific line, but it
                  is in line with Google's own reporting. The column has
                  been added to the UI report alongside a filter option,
                  where users can include and exclude one or many ad sizes.
                </p>
              </div>

              <div>
                <h4>Top-Level Ad Units</h4>

                <p>
                  Users can include and exclude one or many top level
                  ad units by name.
                </p>
              </div>

              <div>
                <h4>Saved Filters</h4>

                <p>
                  Users can
                  <a
                    href="mailto:tech@oao.freshdesk.com"
                    target="_blank"
                  >
                    open a ticket
                  </a>
                  to request user-specific filters at page load.
                </p>
              </div>

              <div v-if="isInternalUser">
                <h4>External User Migration</h4>
                <p>
                  Adds external users to our user base<br>
                  Eases the first-time external user's navigation experience
                  by loading their default at the Pacing route without any
                  user input.<br>
                  Employs GAM team logic to offer team-based reports.<br>
                  Updates the omni-search to allow team-based searches.<br>
                  Updates the authorization logic to allow multi-team configurations.<br>
                  Updates error messaging at SignIn &amp; SignOut to guide
                  users through authentication and authorization, as well as
                  error states they may enter in the process.
                </p>
              </div>

              <div>
                <h4>Stability &amp; Experience Enhancements</h4>

                <p>
                  Reenforces filter logic around operators to assure against bugs and regressions.<br>
                  For consistency, replaces "Local" label with "1st Party" in Discrepancy Creatives Report<br>
                  Extends User Docs<br>
                  Improves multi-device testing<br>
                  Iterates toward stricter security<br>
                  Fixes styling and layout errors impacting modals<br>
                  Fixes sorting indicator issues<br>
                  Refactors internal utilities<br>
                </p>
              </div>

              <div>
                <h4>Browser-Level Enhancements</h4>
                <p>
                  To assure users have the latest and greatest each time
                  they engage with the application, we’ve optimized the
                  browser experience. Users are now prompted to act when
                  sessions expire or the server updates with new data.
                  This is especially useful for users who keep iAdOps
                  open all day and were encountering errors on form
                  submissions due to extended idle times.
                </p>

                <p>
                  Prompts Sign In When Idle Time Exceeds 30 Minutes<br>
                  Prompts Sign In When Auth State Changes<br>
                  Prompts Refresh When New Content from Server<br>
                  <br>
                  Supports Chrome Mobile for iOS and Android<br>
                  Improves Cacheing Experience with Versioning<br>
                </p>
              </div>

              <div>
                <h4>Assures Discrepancy Table Headers Remain Visible on Scroll</h4>
                <p>
                  Users need to maintain column context as they scroll
                  through data-dense tables. To do this, we’ve pinned
                  the Discrepancy table’s headers to the top of the page.
                  The user can scroll through numerous rows without losing
                  sight of column names.
                </p>
              </div>

              <div>
                <h4>Improves Loading Experience at Time and Overview Pages</h4>
                <p>
                  The views were rendering before the data was ready.
                  We’ve since attached a loader experience to these
                  pages to assure content doesn’t render until it’s ready.
                </p>
              </div>

              <div>
                <h4>Enforces Filter Rules: Assert Exclusion over Inclusions</h4>
                <p>
                  We received a bug report revealing the combination of
                  OSI values NEQ 0 and LTE 96 did not exclude 0 OSIs
                  from the result set. This has been resolved and granted
                  thorough testing to assert expectations.
                </p>
              </div>

              <div>
                <h4>Replaces Time Type Operator INCL/EXCL with EQ/NEQ</h4>
                <p>
                  The Time-related filter forms include a Time Type filed
                  that was using INCL/EXCL, as other text inputs are.
                  However, it required a “strict” set of operators, EQ/NEQ,
                  in place of INCL/EXCL.
                </p>

                <p>
                  This addresses the reported cases of, for example,
                  NON-BILLABLE appearing in response to INCL BILLABLE.
                  Though it’s not a bug, it’s not what the user expects;
                  they expect EQ/NEQ.
                </p>
              </div>

              <div>
                <h4>Adds Feedback Form to Header Menu</h4>
                <p>
                  Users are encouraged to leave feedback from anywhere
                  in the application. Clicking the icon in the upper
                  right-most of the screen opens a feedback form in
                  a new tab.
                </p>

                <v-img
                  class="justify-self-center elevation-3 mb-6"
                  src="../../assets/images/release_202008_feedback-form.png"
                  alt=""
                  height="auto"
                  max-width="300px"
                />
              </div>
            </div>

            <div
              id="2020-08"
            >
              <h3
                class="mb-3 subtitle-1 primary--text font-weight-bold"
              >
                August 2020
              </h3>

              <div>
                <h4>Header Icons Displays Browser Tooltip On Hover</h4>
                <p>
                  Hovering over the header menu’s icons will produce a
                  browser tooltip with the icon’s title. This addresses
                  accessibility concerns. Future iterations will replace
                  the native tooltip with a customized tooltip branded
                  for our application.
                </p>
              </div>

              <div>
                <h4>Filters Reset When Navigating Between Clients</h4>
                <p>
                  Any filters set, including Saved Filters, will be unset
                  when the user navigates to another client, typically
                  done via OmniSearch.
                </p>
              </div>

              <div>
                <h4>User Permissions</h4>
                <p>
                  We’ve implemented User Permissions to offer user-specific
                  experiences. This lays the groundwork for Settings our
                  users can use to customize their experience and reports.
                </p>
              </div>
            </div>

            <div
              id="2020-07"
            >
              <h3
                class="mb-3 subtitle-1 primary--text font-weight-bold"
              >
                July 2020
              </h3>

              <div>
                <h4>Beta Releases</h4>

                <p>
                  Data Freshness Timestamp on Pacing and Discrepancy<br>
                  Time-Based OSI<br>
                  Discrepancy Percentage Calculation Refinements<br>
                  Discrepancy Percentage Filter Updates, incl. New Range Options<br>
                  Exclude/Include Options on Text Fields<br>
                  Numerical Operators EQ, NEQ, GTE, and LTE<br>
                  Boolean Operators IS and NOT<br>
                  Editable Numerical Values<br>
                  Impressions Yesterday Field<br>
                  Saved Filters Update the Filter Form<br>
                  Search Instructions and Prominent Input on the Welcome Page<br>
                  Saved Filters’ Info Dialogs for In-Context Information<br>
                  Saved Filters’ Rules Refined. See Definitions Defined In-Context<br>
                  Missing Creatives Column<br>
                  Missing Creatives Filter to Pacing Filter Form<br>
                  User Docs Link Appears in Search Dropdown<br>
                  User Docs Gains Prominent Position and New Icon in Drawer<br>
                  User Docs Expanded for External User Onboarding<br>
                </p>
              </div>
            </div>

            <div
              id="2020-06"
            >
              <h3
                class="mb-3 subtitle-1 primary--text font-weight-bold"
              >
                June 2020
              </h3>

              <div>
                <h4>Beta Releases</h4>

                <p>
                  GAM Network Teams, Available via OmniSearch<br>
                  Added Pacing At Risk as a Saved Filter<br>
                  Saved Filters Toggle on Click<br>
                  Options Update Live to Represent the Report’s Full Data Set<br>
                  Filters Indicator Informs Users When Filters are Set<br>
                  Exclusion Filter Options<br>
                  OSI Calculation Refinements<br>
                  Discrepancy Percentage Error Handling<br>
                  Discrepancy Percentage Filter<br>
                  Exposed the Status Column<br>
                  Added Status Filter to the Delivery Filter Form<br>
                  Draggable Dialogs for Pacing and Discrepancy Sub-Reports<br>
                  Order Count on Click of Saved Filters<br>
                  Time Summaries Update Live on Add, Edit, and Delete of an Entry<br>
                  Assert a 250 Character Limit on Notes<br>
                  Added Weekly Time Totals<br>
                  Added Daily Time Totals<br>
                  Added Monthly Time Total (No Breakdown)<br>
                  Added Overview Summaries<br>
                  Corrected Color Swatch Applied to OSI & Discrepancy Percentage<br>
                  AlphaNumerical Sorting of Dropdown Options<br>
                  Max-Width Set on Dropdown Menus<br>
                  Consistent Handling of Time Entries Across All Timezones<br>
                  Month Label on Time Summaries<br>
                </p>
              </div>

              <div>
                <h4>Line Item Exclusions</h4>

                <p>
                  - status is draft<br>
                  - status is archived<br>
                  - end date > 10 days ago + delivery has not been<br>
                  - start date > 14 days from now<br>
                  - pre-bid orders/line items as indicated by order label<br>
                </p>
              </div>
            </div>

            <div
              id="2020-05"
            >
              <h3
                class="mb-3 subtitle-1 primary--text font-weight-bold"
              >
                May 2020
              </h3>

              <div>
                <h4>Beta Releases</h4>

                <p>
                  Creative-Level Discrepancy with Creative Deep Links<br>
                  Expanded Discrepancy Legend with Calculations<br>
                  OmniSearch<br>
                  Line Item Exclusions (details above)<br>
                  Hours Duration<br>
                  Internal Data Filters<br>
                  Organization Profile Page with MetaData Exposed<br>
                  Person Profile Page with MetaData Exposed<br>
                  Project Profile Page with MetaData Exposed<br>
                  MultiSelect Smart Search<br>
                  Date Input UX Improvements<br>
                  UX &amp; Performance Improvements at Page Load<br>
                  UX &amp; Performance Improvements at Saved Reports<br>
                  Minor Dependency Updates<br>
                  QA &amp; Automated Testing Updates<br>
                  2020 Roadmap Updates<br>
                </p>
              </div>
            </div>

            <div
              id="2020-04"
            >
              <h3
                class="mb-3 subtitle-1 primary--text font-weight-bold"
              >
                March - April 2020
              </h3>

              <div>
                <h4>Beta Releases</h4>

                <p>
                  Export Data from Any Report View<br>
                  Extended API Endpoints<br>
                  Stability &amp; Performance Improvements<br>
                  Complete Re-Architecting of UI for Non-Delivery Data<br>
                  UX Improvements at Loading<br>
                  UX Improvements at Forms<br>
                  UX Improvements at Auth Error States<br>
                  Minor Dependency Updates<br>
                  QA &amp; Automated Testing Updates<br>
                  2020 Roadmap Updates<br>
                </p>
              </div>
            </div>

            <div
              id="2020-02"
            >
              <h3
                class="mb-3 subtitle-1 primary--text font-weight-bold"
              >
                February 2020
              </h3>

              <div>
                <h4>Beta Releases</h4>

                <p>
                  Preview Creatives<br>
                  Get Tearsheets<br>
                  Priority Mult-Select<br>
                  Slimmer Drawer with Flat Navigation<br>
                  Press Enter to Submit Filter<br>
                  Improved Filter Experience<br>
                  Improved Table Responsiveness<br>
                  Updated OSI Logic<br>
                  Improved Performance<br>
                  Minor Dependency Updates<br>
                  Database Migrations<br>
                  GAM API Facade<br>
                  Tearsheets API<br>
                  Test Coverage Reporting<br>
                  2020 Roadmap Updates<br>
                  Fixes Horizontal Scrolling<br>
                  Fixes Vertical Scrolling<br>
                </p>
              </div>
            </div>

            <div
              id="2020-01"
            >
              <h3
                class="mb-3 subtitle-1 primary--text font-weight-bold"
              >
                January 2020
              </h3>

              <div>
                <h4>Beta Releases</h4>

                <p>
                  Discrepancy Report<br>
                  AdJuster ETL Backend Optimizations<br>
                  ETL Process Improvements Targetting Overhead<br>
                  User Activity Tracking thru Google Analytics<br>
                  Master Data Organization and Person Data Iterations<br>
                  Viewability Percentage Rounding to 10th Place<br>
                  Viewability Percentage API Output Fix<br>
                  Browser Cache Busting<br>
                  Browser Performance Improvements<br>
                  Progressive Web App Capabilities, incl. Install<br>
                  Offline Access<br>
                  Improved Mobile Responsiveness<br>
                  New Layout &amp; Full-Screen Table View for Better Data Analysis<br>
                  Improved Filter Experience<br>
                  Major Dependency Updates<br>
                  State Management Refactors<br>
                  Test Coverage Reporting<br>
                  2020 Roadmap Updates<br>
                  Homepage Error Message on Auth Fail<br>
                  Captures OSI Edge Cases<br>
                  Captures Booked Edge Cases<br>
                </p>
              </div>
            </div>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import { createNamespacedHelpers } from 'vuex'
import themeClasses from '@/mixins/themeClasses'

const { mapGetters } = createNamespacedHelpers('auth')

export default {
  name: 'ReleaseNotes',
  mixins: [themeClasses],
  data() {
    return {
      activeSection: '2026-08',
      observer: null,
    }
  },
  computed: {
    ...mapGetters([
      'isInternalUser',
      'isUserInternalDev',
    ]),
    nearblackTextClass() {
      return this.themeTextClass('nearblack--text', 'white--text')
    },

    tocSections() {
      const sections = [
        { id: '2026-08', label: 'August 2026' },
        { id: '2026-06', label: 'June 2026' },
        { id: '2026-05', label: 'May 2026' },
        { id: '2026-04', label: 'April 2026' },
        { id: '2026-03', label: 'March 2026' },
        { id: '2026-02', label: 'February 2026' },
        { id: '2025-11', label: 'November 2025' },
        { id: '2025-06', label: 'June 2025' },
        { id: '2025-03', label: 'March 2025' },
        { id: '2024-12', label: 'December 2024' },
        { id: '2024-09', label: 'September 2024' },
        { id: '2024-06', label: 'June 2024' },
        { id: '2024-03', label: 'March 2024' },
        { id: '2024-01', label: 'January 2024' },
        { id: '2023-10', label: 'October 2023' },
        { id: '2023-08', label: 'August 2023' },
        { id: '2023-05', label: 'May 2023' },
        { id: '2022-06', label: 'June 2022' },
        { id: '2022-05', label: 'May 2022' },
        { id: '2022-04', label: 'April 2022' },
        { id: '2022-03', label: 'March 2022' },
        { id: '2022-02', label: 'February 2022' },
        { id: '2022-01', label: 'Jan – Feb 2022' },
        { id: '2021-12', label: 'December 2021' },
        { id: '2021-11', label: 'November 2021' },
        { id: '2021-10', label: 'October 2021' },
        { id: '2021-09', label: 'September 2021' },
        { id: '2021-08', label: 'August 2021' },
        { id: '2021-07', label: 'July 2021' },
        { id: '2021-06', label: 'June 2021' },
        { id: '2021-05', label: 'May 2021' },
        { id: '2021-04', label: 'April 2021' },
        { id: '2021-03', label: 'March 2021' },
        { id: '2021-02', label: 'February 2021' },
        { id: '2021-01', label: 'January 2021' },
        { id: '2020-12', label: 'December 2020' },
        { id: '2020-11', label: 'November 2020' },
        { id: '2020-10', label: 'Sep – Oct 2020' },
        { id: '2020-08', label: 'August 2020' },
        { id: '2020-07', label: 'July 2020' },
        { id: '2020-06', label: 'June 2020' },
        { id: '2020-05', label: 'May 2020' },
        { id: '2020-04', label: 'Mar – Apr 2020' },
        { id: '2020-02', label: 'February 2020' },
        { id: '2020-01', label: 'January 2020' },
      ]

      // Filter to only sections that exist in the DOM (audience gating may hide some)
      return sections
    },
  },
  mounted() {
    this.$nextTick(() => {
      this.initObserver()
    })
  },
  beforeDestroy() {
    if (this.observer) {
      this.observer.disconnect()
    }
  },
  methods: {
    scrollToSection(id, behavior = 'smooth') {
      const el = document.getElementById(id)
      if (el) {
        el.scrollIntoView({ behavior, block: 'start' })
      }
    },
    initObserver() {
      const options = {
        root: null,
        rootMargin: '-80px 0px -60% 0px',
        threshold: 0,
      }

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            this.activeSection = entry.target.id
          }
        })
      }, options)

      // Observe all section headings
      this.tocSections.forEach((section) => {
        const el = document.getElementById(section.id)
        if (el) {
          this.observer.observe(el)
        }
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.release-notes-page {
  max-width: 1200px;
  margin: 0 auto;
}

// Sticky sidebar TOC
.toc-sidebar {
  border-right: 1px solid rgba(0, 0, 0, 0.08);
}

.toc-sticky {
  position: sticky;
  top: 64px;
  max-height: calc(100vh - 80px);
  overflow-y: auto;

  // Thin custom scrollbar
  &::-webkit-scrollbar {
    width: 3px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.15);
    border-radius: 3px;
  }
}

.toc-list {
  background: transparent !important;
}

.toc-item {
  min-height: 28px !important;
  padding: 0 16px !important;
  border-left: 2px solid transparent;
  transition: border-color 0.2s ease, background-color 0.2s ease;

  &.toc-active {
    border-left-color: #0094ff; // brightblue / primary
    background-color: rgba(0, 148, 255, 0.06);
  }

  &:hover {
    background-color: rgba(0, 0, 0, 0.04);
  }
}

// Badge styling
.release-badge {
  display: inline-block;
  padding: 1px 8px;
  margin-right: 6px;
  border-radius: 3px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  vertical-align: middle;
  line-height: 18px;
}

// Entry spacing
.release-entry {
  margin-bottom: 16px;

  h4 {
    margin-bottom: 4px;
    font-size: 14px;
    line-height: 1.6;
  }

  p {
    font-size: 13px;
    line-height: 1.55;
    margin-bottom: 8px;
  }

  code {
    font-size: 12px;
    padding: 1px 5px;
    border-radius: 3px;
    background-color: rgba(0, 0, 0, 0.05);
    color: #055289; // darkestblue
  }
}

// Month section separators
.release-section {
  padding-bottom: 24px;
  margin-bottom: 24px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}

.release-month-header {
  scroll-margin-top: 80px;
}

// Legacy sections use the container div's id for scrolling
div[id^="20"] {
  scroll-margin-top: 80px;
}
</style>
